import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Plus, Sparkles, Share2, X, Save, Eye, EyeOff, 
  Link2, Image, MessageSquare, Send, ChevronDown, ChevronUp,
  Check, Trash2, ExternalLink, Lightbulb, Clock, MapPin, AlertCircle, Hotel, Edit2,
  Copy, Users, FileText, MessageCircle, Loader2, Shield, CreditCard, CheckCircle2, CheckCircle, GripVertical,
  Plane, DollarSign, Calendar, Bell, Lock, Unlock, Cloud, Sun, CloudRain, Phone, Map as MapIcon, Heart, Mail,
  ClipboardList, StickyNote, Navigation, Search, Coffee, Wine, Landmark, TreePine, ShoppingBag, Waves, Music, Store, Utensils
} from 'lucide-react';
import { VERAULT_ICONS, VERAULT_COLORS, getVeraultIcon } from '../components/VeraultIcons';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast, Toaster } from 'sonner';
import { ItineraryEditor } from '../components/ItineraryEditor';
import { ItineraryBuilder } from '../components/ItineraryBuilder';
import { SmartItineraryBuilder } from '../components/SmartItineraryBuilder';
import { ItineraryEngine } from '../components/ItineraryEngine';
import { TripAIAssistant } from '../components/TripAIAssistant';
import { UnifiedTripDetails } from '../components/UnifiedTripDetails';
import { DestinationSegmentsForm } from '../components/DestinationSegmentsForm';
import { DiscoverPlaces } from '../components/DiscoverPlaces';
import { RoutePlanner } from '../components/RoutePlanner';
import { AutoPlanDay } from '../components/AutoPlanDay';
import { TripTimeline } from '../components/TripTimeline';
import { TripPlanningProvider, DayNavigator, TripDaysSummary, calculateTripDays } from '../contexts/TripPlanningContext';
import { useTheme } from '../contexts/ThemeContext';
import { 
  formatDate, 
  formatDateTime, 
  formatDateRange, 
  getShortWeekday,
  getTripStartDate,
  getTripEndDate,
  getTripDates,
  getTripDateRangeFormatted,
  getTripDayCount,
  getTripDestinationsDisplay
} from '../utils/dateFormat';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to get effective dates - uses single source of truth
function getEffectiveDates(form) {
  return {
    startDate: getTripStartDate(form),
    endDate: getTripEndDate(form)
  };
}

// Helper to format trip date range display
function formatTripDateRange(form) {
  const { startDate, endDate } = getEffectiveDates(form);
  if (!startDate || !endDate) return 'Dates not set';
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
  const totalNights = totalDays - 1;
  
  const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  
  return `${startStr} – ${endStr} (${totalDays} day${totalDays !== 1 ? 's' : ''}, ${totalNights} night${totalNights !== 1 ? 's' : ''})`;
}

// Helper to get destination display string from segments or legacy field
// Uses centralized utility for consistency
function getDestinationDisplayLocal(form) {
  const display = getTripDestinationsDisplay(form);
  return display || 'Destination TBD';
}

// Weather Display Component with F/C Toggle
function WeatherDisplay({ destination, startDate, endDate }) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unit, setUnit] = useState(() => localStorage.getItem('verault_temp_unit') || 'fahrenheit');

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const params = new URLSearchParams();
        if (startDate) params.append('start_date', startDate);
        if (endDate) params.append('end_date', endDate);
        
        const res = await axios.get(`${API}/weather/${encodeURIComponent(destination)}?${params.toString()}`);
        setWeather(res.data);
      } catch (e) {
        setWeather(null);
      } finally {
        setLoading(false);
      }
    };
    if (destination) fetchWeather();
  }, [destination, startDate, endDate]);

  const toggleUnit = () => {
    const next = unit === 'fahrenheit' ? 'celsius' : 'fahrenheit';
    setUnit(next);
    localStorage.setItem('verault_temp_unit', next);
  };

  const formatTemp = (celsius) => {
    if (celsius == null || isNaN(celsius)) return '--';
    if (unit === 'fahrenheit') return `${Math.round(celsius * 9/5 + 32)}°F`;
    return `${Math.round(celsius)}°C`;
  };

  // Extract raw celsius number from API string like "18°C"
  const parseRawTemp = (str) => {
    if (!str) return null;
    const match = String(str).match(/([-\d.]+)/);
    return match ? parseFloat(match[1]) : null;
  };

  if (loading) {
    return <div className="flex items-center justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-[#8C8C8C]" /></div>;
  }

  if (!weather || weather.error) {
    return (
      <div>
        <p className="text-sm text-[#8C8C8C]">Weather data unavailable</p>
        {weather?.seasonal_guidance && (
          <p className="text-xs text-[#B0B0B0] mt-2">{weather.seasonal_guidance}</p>
        )}
      </div>
    );
  }

  const getWeatherIcon = () => {
    const cond = (weather.current?.condition || weather.condition || '').toLowerCase();
    if (cond.includes('rain') || cond.includes('drizzle') || cond.includes('shower')) return <CloudRain className="w-8 h-8 text-[#8DA399]" />;
    if (cond.includes('cloud') || cond.includes('overcast') || cond.includes('fog')) return <Cloud className="w-8 h-8 text-[#8C8C8C]" />;
    return <Sun className="w-8 h-8 text-[#C8A85A]" />;
  };

  // Parse raw celsius from current.temperature string (e.g., "18°C")
  const rawCelsius = parseRawTemp(weather.current?.temperature);
  const currentCondition = weather.current?.condition || weather.condition;
  const currentHumidity = weather.current?.humidity || weather.humidity;

  return (
    <div className="space-y-4" data-testid="weather-display">
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-[#F9F8F4] flex items-center justify-center">
          {getWeatherIcon()}
        </div>
        <div className="flex-1">
          <p className="font-medium text-[#1A1A1A]">{weather.destination}</p>
          <p className="text-lg font-serif text-[#C8A85A]" data-testid="weather-current-temp">
            {rawCelsius != null ? formatTemp(rawCelsius) : (weather.current?.temperature || 'N/A')}
          </p>
          <p className="text-sm text-[#595959]">{currentCondition}</p>
          {currentHumidity && <p className="text-xs text-[#8C8C8C]">Humidity: {currentHumidity}</p>}
        </div>
        {/* F/C Toggle */}
        <button
          onClick={toggleUnit}
          className="self-start px-2.5 py-1 text-xs font-medium rounded-lg bg-[#F5F1EB] hover:bg-[#EEE9E2] text-[#595959] transition-colors"
          data-testid="weather-unit-toggle"
          title={`Switch to ${unit === 'fahrenheit' ? 'Celsius' : 'Fahrenheit'}`}
        >
          {unit === 'fahrenheit' ? '°F' : '°C'}
        </button>
      </div>
      
      {/* Seasonal Guidance */}
      {weather.seasonal_guidance && (
        <div className="bg-[#F9F8F4] rounded-lg p-3">
          <p className="text-xs text-[#595959]">{weather.seasonal_guidance}</p>
        </div>
      )}
      
      {/* Forecast Preview */}
      {weather.forecast && weather.forecast.length > 0 && (
        <div>
          <p className="text-xs text-[#8C8C8C] uppercase tracking-wide mb-2">7-Day Forecast</p>
          <div className="grid grid-cols-7 gap-1">
            {weather.forecast.slice(0, 7).map((day, idx) => (
              <div key={idx} className="text-center p-1 bg-[#EEE9E2] rounded" data-testid={`forecast-day-${idx}`}>
                <p className="text-xs text-[#8C8C8C]">{getShortWeekday(day.date)}</p>
                <p className="text-xs font-medium text-[#1A1A1A]">{day.high != null ? formatTemp(day.high) : '--'}</p>
                <p className="text-[10px] text-[#8C8C8C]">{day.low != null ? formatTemp(day.low) : ''}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Packing Suggestions */}
      {weather.packing_suggestions && weather.packing_suggestions.length > 0 && (
        <div className="border-t border-[rgba(0,0,0,0.06)] pt-3 mt-3">
          <p className="text-xs text-[#8C8C8C] uppercase tracking-wide mb-2">Packing Suggestions</p>
          <div className="flex flex-wrap gap-1">
            {weather.packing_suggestions.map((item, idx) => (
              <span key={idx} className="text-xs px-2 py-1 bg-[#C8A85A]/10 text-[#C8A85A] rounded-full">{item}</span>
            ))}
          </div>
        </div>
      )}
      
      <p className="text-xs text-[#B0B0B0]">Source: {weather.source === 'open-meteo' ? 'Open-Meteo (Live)' : 'Estimated'}</p>
    </div>
  );
}

// Planning Fee Status Component
function PlanningFeeStatus({ tripId, form }) {
  const [planningStatus, setPlanningStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${API}/travels/trips/${tripId}/planning-status`);
        setPlanningStatus(res.data);
      } catch (e) {
        console.error('Failed to fetch planning status', e);
      } finally {
        setLoading(false);
      }
    };
    if (tripId) fetchStatus();
  }, [tripId]);

  const handleSetPlan = async (plan) => {
    try {
      const res = await axios.post(`${API}/travels/trips/${tripId}/set-payment-plan?plan=${plan}`);
      setPlanningStatus(prev => ({ ...prev, planning_payment_plan: plan, planning_payments: res.data.payments }));
      toast.success(`Payment plan set to ${plan}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to set payment plan');
    }
  };

  const handleRecordPayment = async (paymentId) => {
    try {
      const res = await axios.post(`${API}/travels/trips/${tripId}/record-planning-payment?payment_id=${paymentId}`);
      setPlanningStatus(prev => ({
        ...prev,
        planning_fee_status: res.data.planning_fee_status,
        planning_access_unlocked: res.data.planning_access_unlocked,
        planning_payments: prev.planning_payments.map(p => 
          p.id === paymentId ? { ...p, status: 'paid', paid_date: new Date().toISOString().split('T')[0] } : p
        )
      }));
      toast.success(res.data.is_first_payment ? 'First payment received — Planning unlocked!' : 'Payment recorded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleOverride = async () => {
    try {
      await axios.post(`${API}/travels/trips/${tripId}/override-planning-gate?reason=${encodeURIComponent(overrideReason)}&unlock=true`);
      setPlanningStatus(prev => ({ ...prev, planning_access_unlocked: true, planning_gate_override: true }));
      toast.success('Planning access unlocked (override)');
      setShowOverrideDialog(false);
    } catch (e) {
      toast.error('Failed to override');
    }
  };

  const handleWaiveFee = async () => {
    try {
      await axios.post(`${API}/travels/trips/${tripId}/waive-planning-fee?reason=${encodeURIComponent(overrideReason || 'Waived by advisor')}`);
      setPlanningStatus(prev => ({ ...prev, planning_fee_waived: true, planning_access_unlocked: true }));
      toast.success('Planning fee waived');
      setShowOverrideDialog(false);
    } catch (e) {
      toast.error('Failed to waive fee');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-[#8C8C8C]" /></div>;
  }

  if (!planningStatus) return null;

  const { 
    planning_fee_amount, planning_fee_waived, planning_payment_plan, 
    planning_payments, planning_fee_status, planning_access_unlocked,
    allowed_payment_plans, days_until_departure, total_paid, total_remaining,
    planning_gate_override, booking_authorization_status
  } = planningStatus;

  const statusColors = {
    'not_paid': 'bg-[#B37E6B]/10 text-[#B37E6B]',
    'partial': 'bg-[#C8A85A]/10 text-[#C8A85A]',
    'paid': 'bg-[#8DA399]/10 text-[#8DA399]',
    'waived': 'bg-[#8DA399]/10 text-[#8DA399]'
  };

  return (
    <div className="space-y-4">
      {/* Status Header */}
      <div className="flex items-center justify-between">
        <div>
          <span className={`text-xs px-2 py-1 rounded-full ${statusColors[planning_fee_waived ? 'waived' : planning_fee_status]}`}>
            {planning_fee_waived ? 'Waived' : planning_fee_status === 'paid' ? 'Paid' : planning_fee_status === 'partial' ? 'Partial' : 'Pending'}
          </span>
          {planning_gate_override && (
            <span className="text-xs px-2 py-1 rounded-full bg-[#C8A85A]/10 text-[#C8A85A] ml-2">Override Active</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {planning_access_unlocked ? (
            <span className="text-xs text-[#8DA399] flex items-center gap-1"><Unlock className="w-3 h-3" /> Unlocked</span>
          ) : (
            <span className="text-xs text-[#B37E6B] flex items-center gap-1"><Lock className="w-3 h-3" /> Locked</span>
          )}
        </div>
      </div>

      {/* Fee Amount & Timeline */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-[#EEE9E2] rounded-lg p-3">
          <p className="text-xs text-[#8C8C8C]">Planning Fee</p>
          <p className="font-serif text-lg text-[#1A1A1A]">${planning_fee_amount.toLocaleString()}</p>
        </div>
        <div className="bg-[#EEE9E2] rounded-lg p-3">
          <p className="text-xs text-[#8C8C8C]">Days to Departure</p>
          <p className="font-serif text-lg text-[#1A1A1A]">{days_until_departure}</p>
        </div>
      </div>

      {/* Payment Plan Selector */}
      {!planning_fee_waived && planning_fee_status === 'not_paid' && (
        <div>
          <p className="text-xs text-[#8C8C8C] uppercase tracking-wide mb-2">Payment Plan</p>
          <div className="flex gap-2">
            {['full', '2-part', '3-part'].map((plan) => (
              <button
                key={plan}
                onClick={() => handleSetPlan(plan)}
                disabled={!allowed_payment_plans.includes(plan)}
                className={`flex-1 px-3 py-2 rounded-lg text-sm border transition-colors ${
                  planning_payment_plan === plan 
                    ? 'border-[#C8A85A] bg-[#C8A85A]/10 text-[#C8A85A]'
                    : allowed_payment_plans.includes(plan)
                      ? 'border-[rgba(0,0,0,0.1)] hover:border-[#C8A85A]/50'
                      : 'border-[rgba(0,0,0,0.06)] bg-[#F5F5F5] text-[#B0B0B0] cursor-not-allowed'
                }`}
              >
                {plan === 'full' ? 'Pay in Full' : plan === '2-part' ? '2 Payments' : '3 Payments'}
              </button>
            ))}
          </div>
          {days_until_departure < 30 && (
            <p className="text-xs text-[#B37E6B] mt-2">⚠️ Under 30 days — Full payment required</p>
          )}
        </div>
      )}

      {/* Payment Schedule */}
      {planning_payments && planning_payments.length > 0 && !planning_fee_waived && (
        <div>
          <p className="text-xs text-[#8C8C8C] uppercase tracking-wide mb-2">Payment Schedule</p>
          <div className="space-y-2">
            {planning_payments.map((payment, idx) => (
              <div key={payment.id} className="flex items-center justify-between p-3 bg-[#EEE9E2] rounded-lg">
                <div>
                  <p className="text-sm font-medium text-[#1A1A1A]">{payment.description}</p>
                  <p className="text-xs text-[#8C8C8C]">Due: {payment.due_date}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-medium">${payment.amount.toLocaleString()}</span>
                  {payment.status === 'paid' ? (
                    <span className="text-xs px-2 py-1 rounded-full bg-[#8DA399]/10 text-[#8DA399]">Paid</span>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => handleRecordPayment(payment.id)}
                      className="bg-[#C8A85A] hover:bg-[#B39049] text-white text-xs"
                    >
                      Mark Paid
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Totals */}
      {!planning_fee_waived && planning_payments && planning_payments.length > 0 && (
        <div className="flex justify-between pt-3 border-t border-[rgba(0,0,0,0.06)]">
          <span className="text-sm text-[#595959]">Paid: ${total_paid.toLocaleString()}</span>
          <span className="text-sm font-medium text-[#1A1A1A]">Remaining: ${total_remaining.toLocaleString()}</span>
        </div>
      )}

      {/* Gating Message */}
      {!planning_access_unlocked && (
        <div className="bg-[#B37E6B]/10 rounded-lg p-4 border border-[#B37E6B]/20">
          <p className="text-sm text-[#B37E6B] font-medium mb-1">Planning Access Locked</p>
          <p className="text-xs text-[#B37E6B]/80">
            Planning begins once the initial planning fee has been received. AI Assistant, Itinerary, and Client Portal are locked.
          </p>
        </div>
      )}

      {/* Admin Override */}
      <div className="flex gap-2 pt-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowOverrideDialog(true)}
          className="text-xs"
        >
          Admin Override
        </Button>
      </div>

      {/* Override Dialog */}
      {showOverrideDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h3 className="font-serif text-lg text-[#1A1A1A] mb-4">Admin Override</h3>
            <Textarea
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder="Reason for override (logged internally)..."
              className="mb-4"
            />
            <div className="flex gap-2">
              <Button onClick={handleOverride} className="flex-1 bg-[#2C2C2C]">
                Unlock Planning
              </Button>
              <Button onClick={handleWaiveFee} variant="outline" className="flex-1">
                Waive Fee
              </Button>
              <Button onClick={() => setShowOverrideDialog(false)} variant="ghost">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Booking Authorization Status */}
      {planning_access_unlocked && (
        <div className="pt-3 border-t border-[rgba(0,0,0,0.06)]">
          <p className="text-xs text-[#8C8C8C] uppercase tracking-wide mb-2">Booking Authorization</p>
          <span className={`text-xs px-2 py-1 rounded-full ${
            booking_authorization_status === 'booked' ? 'bg-[#8DA399]/10 text-[#8DA399]' :
            booking_authorization_status === 'authorized' ? 'bg-[#8DA399]/10 text-[#8DA399]' :
            booking_authorization_status === 'submitted' ? 'bg-[#C8A85A]/10 text-[#C8A85A]' :
            'bg-[#E6E4DE] text-[#8C8C8C]'
          }`}>
            {booking_authorization_status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </span>
        </div>
      )}
    </div>
  );
}

const TRAVEL_STYLE_OPTIONS = [
  { value: 'luxury', label: 'Luxury' },
  { value: 'relaxed', label: 'Relaxed' },
  { value: 'adventure', label: 'Adventure' },
  { value: 'food', label: 'Food' },
  { value: 'culture', label: 'Culture' },
  { value: 'wellness', label: 'Wellness' },
  { value: 'romance', label: 'Romance' },
];

const TRIP_TYPES = [
  { value: 'anniversary', label: 'Anniversary' },
  { value: 'group', label: 'Group' },
  { value: 'cruise', label: 'Cruise' },
  { value: 'family', label: 'Family' },
  { value: 'honeymoon', label: 'Honeymoon' },
  { value: 'solo', label: 'Solo' },
  { value: 'business', label: 'Business' },
  { value: 'wellness', label: 'Wellness Retreat' },
];

const SOPHISTICATED_EMOJIS = ['🍽️', '🖼️', '🏖️', '🍸', '🛍️', '🧖', '🎭', '🌅', '🏛️', '🚢', '✨', '🌿'];

const SECTION_COLORS = {
  morning: { bg: 'bg-[#C8A85A]/10', text: 'text-[#C8A85A]', border: 'border-[#C8A85A]/20' },
  afternoon: { bg: 'bg-[#8DA399]/10', text: 'text-[#8DA399]', border: 'border-[#8DA399]/20' },
  evening: { bg: 'bg-[#B37E6B]/10', text: 'text-[#B37E6B]', border: 'border-[#B37E6B]/20' },
};

const REQUEST_STATUS_COLORS = {
  new: 'bg-[#B37E6B]/10 text-[#B37E6B]',
  in_progress: 'bg-[#C8A85A]/10 text-[#C8A85A]',
  resolved: 'bg-[#8DA399]/10 text-[#8DA399]',
};

// Workflow Stage Configuration
const WORKFLOW_STAGES = {
  consultation_complete: { label: 'Consultation Complete', color: 'bg-[#8C8C8C]', icon: '📋' },
  awaiting_planning_fee: { label: 'Awaiting Planning Fee', color: 'bg-[#C8A85A]', icon: '💳' },
  planning_in_progress: { label: 'Planning In Progress', color: 'bg-[#8DA399]', icon: '✏️' },
  client_review: { label: 'Client Review', color: 'bg-[#B37E6B]', icon: '👁️' },
  trip_confirmed: { label: 'Trip Confirmed', color: 'bg-[#2C8A4B]', icon: '✅' },
  traveling: { label: 'Traveling', color: 'bg-[#2C2C2C]', icon: '✈️' },
  completed: { label: 'Completed', color: 'bg-[#6B5B95]', icon: '🏆' },
};

const WORKFLOW_STAGE_ORDER = [
  'consultation_complete',
  'awaiting_planning_fee',
  'planning_in_progress',
  'client_review',
  'trip_confirmed',
  'traveling',
  'completed'
];

// Client Tab Options
const CLIENT_TAB_OPTIONS = [
  { id: 'home', label: 'Home' },
  { id: 'itinerary', label: 'Itinerary' },
  { id: 'estimate', label: 'Estimate' },
  { id: 'logistics', label: 'Logistics' },
  { id: 'agreements', label: 'Agreements' },
  { id: 'chat', label: 'Messages' },
];

// ==================== MASTER LIFECYCLE CONFIGURATION ====================
// 8-stage story-based lifecycle - SINGLE SOURCE OF TRUTH
// Using VÉRAULT SVG icons - NO "Draft", elegant client-facing language
const MASTER_LIFECYCLE = {
  planning_initiated: {
    label: 'Planning Initiated',
    client_label: 'Your journey is taking shape',
    story: 'Your journey begins',
    iconName: 'sparkle',
    color: '#8C8A86',
    waiting_on: [],
    order: 1
  },
  details_gathering: {
    label: 'Details Gathering',
    client_label: "We're gathering your travel details",
    story: 'Gathering your details',
    iconName: 'clipboard',
    color: '#C8A85A',
    waiting_on: ['intake_complete'],
    order: 2,
    gates_itinerary: true
  },
  design_in_progress: {
    label: 'Design in Progress',
    client_label: 'Your itinerary is being designed',
    story: 'Designing your experience',
    iconName: 'pencil',
    color: '#8C8A86',
    waiting_on: ['itinerary'],
    order: 3
  },
  itinerary_ready: {
    label: 'Itinerary Ready for Review',
    client_label: 'Your itinerary is ready for your review',
    story: 'Your itinerary awaits',
    iconName: 'eye',
    color: '#C8A85A',
    waiting_on: ['client_approval'],
    order: 4
  },
  booking_in_progress: {
    label: 'Booking in Progress',
    client_label: "We're securing your reservations",
    story: 'Securing your reservations',
    iconName: 'clock',
    color: '#C8A85A',
    waiting_on: ['bookings_complete'],
    order: 5
  },
  trip_confirmed: {
    label: 'Trip Confirmed',
    client_label: 'Your trip is confirmed',
    story: 'Your journey is confirmed',
    iconName: 'plane',
    color: '#8DA399',
    waiting_on: [],
    show_countdown: true,
    order: 6
  },
  in_travel: {
    label: 'In Travel',
    client_label: 'Enjoy your journey',
    story: 'Your adventure unfolds',
    iconName: 'globe',
    color: '#1A1A1A',
    waiting_on: [],
    order: 7
  },
  completed: {
    label: 'Completed',
    client_label: 'Welcome home',
    story: 'Welcome home',
    iconName: 'heart',
    color: '#8C8A86',
    waiting_on: [],
    is_archive: true,
    order: 8
  }
};

const LIFECYCLE_ORDER = [
  'planning_initiated',
  'details_gathering',
  'design_in_progress',
  'itinerary_ready',
  'booking_in_progress',
  'trip_confirmed',
  'in_travel',
  'completed'
];

// ==================== DESTINATION SEARCH COMPONENT ====================
function DestinationSearch({ value, onChange, onSelect }) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchTimeout = useRef(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  const searchLocation = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/travels/location/search`, {
        params: { query: searchQuery, limit: 5 }
      });
      setResults(response.data.results || []);
      setShowResults(true);
    } catch (err) {
      console.error('Location search error:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange(val);

    // Debounce search
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      searchLocation(val);
    }, 300);
  };

  const handleSelect = (location) => {
    setQuery(location.display_name);
    onChange(location.display_name);
    onSelect(location);
    setShowResults(false);
    setResults([]);
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <Input
          value={query}
          onChange={handleInputChange}
          onFocus={() => results.length > 0 && setShowResults(true)}
          onBlur={() => setTimeout(() => setShowResults(false), 200)}
          placeholder="Search destination..."
          className="bg-white text-sm flex-1"
        />
        {loading && <Loader2 className="w-4 h-4 animate-spin text-[#8C8A86]" />}
      </div>
      
      {showResults && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[rgba(0,0,0,0.1)] rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
          {results.map((loc, idx) => (
            <button
              key={idx}
              onClick={() => handleSelect(loc)}
              className="w-full px-3 py-2 text-left text-sm hover:bg-[#F5F1EB] transition-colors border-b border-[rgba(0,0,0,0.05)] last:border-0"
            >
              <div className="font-medium text-[#1A1A1A]">{loc.display_name}</div>
              {loc.country && (
                <div className="text-xs text-[#8C8A86] flex items-center gap-1 mt-0.5">
                  <MapPin className="w-3 h-3" />
                  {loc.country_code && <span className="font-mono">{loc.country_code}</span>}
                  {loc.country_code === 'US' ? 'Domestic' : 'International'}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== INTAKE ACTIONS COMPONENT ====================
function IntakeActions({ tripId, intakeStatus, actions, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [intakeUrl, setIntakeUrl] = useState(null);
  
  const handleSendIntake = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/travels/trips/${tripId}/intake/send`);
      if (res.data.intake_url) {
        setIntakeUrl(res.data.intake_url);
        toast.success('Intake link generated and sent!');
        if (onSuccess) onSuccess();
      } else if (res.data.already_sent) {
        toast.info(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send intake');
    } finally {
      setLoading(false);
    }
  };
  
  const handleGenerateLink = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/travels/trips/${tripId}/intake/generate-link`);
      if (res.data.intake_path) {
        const fullUrl = `${window.location.origin}${res.data.intake_path}`;
        setIntakeUrl(fullUrl);
        navigator.clipboard.writeText(fullUrl);
        toast.success('Intake link copied to clipboard!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate link');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSkipIntake = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/travels/trips/${tripId}/intake/skip`);
      toast.success('Intake marked as not needed');
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error('Failed to skip intake');
    } finally {
      setLoading(false);
    }
  };
  
  const copyToClipboard = () => {
    if (intakeUrl) {
      navigator.clipboard.writeText(intakeUrl);
      toast.success('Link copied!');
    }
  };
  
  const INTAKE_STATUS_MAP = {
    'not_sent': 'Not Sent',
    'sent': 'Sent - Awaiting Completion',
    'completed': 'Completed',
    'not_needed': 'Not Needed'
  };
  
  return (
    <div className="space-y-3">
      <p className="text-xs text-[#8C8A86]">
        Status: <span className="font-medium text-[#1A1A1A]">{INTAKE_STATUS_MAP[intakeStatus] || intakeStatus}</span>
      </p>
      
      {intakeUrl && (
        <div className="p-2 bg-[#F5F1EB] rounded-lg">
          <p className="text-xs text-[#8C8A86] mb-1">Intake Link (click to copy):</p>
          <button 
            onClick={copyToClipboard}
            className="text-xs text-[#C8A85A] break-all hover:underline text-left"
          >
            {intakeUrl}
          </button>
        </div>
      )}
      
      {intakeStatus === 'not_sent' && (
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={handleSendIntake}
            disabled={loading}
            className="bg-[#C8A85A] hover:bg-[#B39049] text-white flex-1"
            data-testid="send-intake-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
            Send Intake
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleGenerateLink}
            disabled={loading}
            className="border-[#E6E4DE]"
            title="Generate link only (no email)"
          >
            <Link2 className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleSkipIntake}
            disabled={loading}
            className="text-[#8C8C8C]"
            title="Mark as not needed"
          >
            Skip
          </Button>
        </div>
      )}
      
      {intakeStatus === 'sent' && (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleGenerateLink}
            disabled={loading}
            className="border-[#E6E4DE] flex-1"
          >
            <Link2 className="w-4 h-4 mr-1" />
            Copy Link
          </Button>
          <Button
            size="sm"
            onClick={handleSendIntake}
            disabled={loading}
            className="bg-[#8C8A86] hover:bg-[#7A7875] text-white"
          >
            Resend
          </Button>
        </div>
      )}
      
      {(intakeStatus === 'completed' || intakeStatus === 'not_needed') && (
        <p className="text-xs text-[#8DA399]">
          {intakeStatus === 'completed' ? '✓ Client has completed intake' : '✓ Intake skipped by advisor'}
        </p>
      )}
    </div>
  );
}

// ==================== TRIP FORMS PANEL ====================
// Shows form status and quick send actions for the trip
function TripFormsPanel({ tripId, clientId }) {
  const [formData, setFormData] = useState({ instances: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null);

  useEffect(() => {
    if (tripId) fetchForms();
  }, [tripId]);

  const fetchForms = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/forms/trip/${tripId}`);
      setFormData(res.data);
    } catch (e) {
      console.error('Failed to fetch forms', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSendForm = async (templateId) => {
    setSending(templateId);
    try {
      // Create instance
      const instanceRes = await axios.post(`${API}/forms/instances`, {
        template_id: templateId,
        trip_id: tripId,
        client_id: clientId
      });
      
      // Send it
      const sendRes = await axios.post(`${API}/forms/instances/${instanceRes.data.id}/send`);
      
      // Copy link
      if (sendRes.data.form_link) {
        navigator.clipboard?.writeText(sendRes.data.form_link);
        toast.success('Form sent! Link copied to clipboard');
      } else {
        toast.success('Form sent!');
      }
      
      fetchForms();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send form');
    } finally {
      setSending(null);
    }
  };

  const FORM_BUTTONS = [
    { id: 'travel_planning_agreement', label: 'Agreement', shortLabel: 'Agreement' },
    { id: 'terms_conditions', label: 'T&C', shortLabel: 'T&C' },
    { id: 'travel_insurance', label: 'Insurance', shortLabel: 'Insurance' },
    { id: 'credit_card_auth', label: 'CC Auth', shortLabel: 'CC Auth' }
  ];

  const getFormStatus = (templateId) => {
    const templateData = formData.summary?.by_template?.[templateId];
    if (!templateData) return null;
    return templateData.latest_status;
  };

  const STATUS_COLORS = {
    draft: { bg: 'bg-[#E6E4DE]', text: 'text-[#8C8A86]' },
    sent: { bg: 'bg-[#C8A85A]/20', text: 'text-[#C8A85A]' },
    viewed: { bg: 'bg-[#C8A85A]/30', text: 'text-[#C8A85A]' },
    completed: { bg: 'bg-[#8DA399]/20', text: 'text-[#8DA399]' }
  };

  return (
    <div className="bg-[#F5F1EB] rounded-xl p-4 space-y-3" data-testid="trip-forms-panel">
      <div className="flex items-center gap-2">
        <FileText className="w-4 h-4 text-[#C8A85A]" />
        <p className="text-xs font-medium uppercase tracking-wide text-[#8C8A86]">Forms</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="w-4 h-4 animate-spin text-[#C8A85A]" />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {FORM_BUTTONS.map(form => {
            const status = getFormStatus(form.id);
            const statusStyle = STATUS_COLORS[status] || {};
            const isSending = sending === form.id;
            
            return (
              <button
                key={form.id}
                onClick={() => handleSendForm(form.id)}
                disabled={isSending}
                className={`relative px-3 py-2 rounded-lg text-sm font-medium transition-all 
                  ${status === 'completed' 
                    ? 'bg-[#8DA399]/10 text-[#8DA399] cursor-default' 
                    : 'bg-white hover:bg-[#F9F8F4] text-[#1A1A1A] border border-[#E6E4DE]'
                  }`}
                data-testid={`send-form-${form.id}`}
              >
                <span className="flex items-center justify-center gap-1">
                  {isSending ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : status === 'completed' ? (
                    <CheckCircle className="w-3 h-3" />
                  ) : (
                    <Send className="w-3 h-3" />
                  )}
                  {form.shortLabel}
                </span>
                
                {/* Status indicator */}
                {status && status !== 'draft' && (
                  <span className={`absolute -top-1 -right-1 w-2 h-2 rounded-full ${
                    status === 'completed' ? 'bg-[#8DA399]' :
                    status === 'viewed' ? 'bg-[#C8A85A]' :
                    status === 'sent' ? 'bg-[#E6E4DE]' : ''
                  }`} />
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Recent form instances */}
      {formData.instances?.length > 0 && (
        <div className="pt-2 border-t border-[#E6E4DE]">
          <p className="text-xs text-[#8C8A86] mb-2">Recent</p>
          <div className="space-y-1">
            {formData.instances.slice(0, 3).map(inst => (
              <div 
                key={inst.id} 
                className="flex items-center justify-between p-2 bg-white rounded text-xs"
              >
                <span className="text-[#1A1A1A]">
                  {inst.template_id === 'travel_planning_agreement' ? 'Agreement' :
                   inst.template_id === 'terms_conditions' ? 'T&C' :
                   inst.template_id === 'travel_insurance' ? 'Insurance' :
                   inst.template_id === 'credit_card_auth' ? 'CC Auth' : inst.template_id}
                </span>
                <span className={`px-2 py-0.5 rounded ${STATUS_COLORS[inst.status]?.bg || ''} ${STATUS_COLORS[inst.status]?.text || ''}`}>
                  {inst.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== COMMAND BOX COMPONENT ====================
// Uniform card-style box with 3 interaction states:
// State 1: Collapsed (summary) - default
// State 2: Expanded (inline edit) - single click/tap
// State 3: Navigate - double click or explicit open action
function CommandBox({ 
  box, 
  onNavigate, 
  onSave,
  tripId,
  form,
  isExpanded,
  onToggleExpand 
}) {
  const [editValue, setEditValue] = useState(null);
  const [saving, setSaving] = useState(false);
  const lastClickRef = useRef(0);
  
  const statusColors = {
    complete: 'bg-[#8DA399]/10',
    incomplete: 'bg-[#EEE9E2]',
    pending: 'bg-[#EEE9E2]',
    info: 'bg-[#EEE9E2]'
  };
  
  // VÉRAULT icon mapping for box types
  const boxIconMap = {
    travelers: 'travelers',
    dates: 'calendar',
    destination: 'mapPin',
    intake: 'clipboard',
    itinerary: 'document',
    estimate: 'dollar',
    agreement: 'shield',
    weather: 'weather',
    countdown: 'clock'
  };
  
  // Status icon colors (VÉRAULT palette)
  const statusIconColors = {
    complete: VERAULT_COLORS.stone,
    incomplete: VERAULT_COLORS.stone,
    pending: VERAULT_COLORS.gold,
    info: VERAULT_COLORS.stone
  };
  
  // Get the appropriate icon for this box with status coloring
  const getBoxIcon = () => {
    const iconName = boxIconMap[box.id] || 'document';
    const color = box.status === 'complete' 
      ? '#8DA399'  // Green-ish for complete
      : statusIconColors[box.status] || VERAULT_COLORS.stone;
    return getVeraultIcon(iconName, { size: 18, color });
  };
  
  const handleClick = () => {
    const now = Date.now();
    const timeSinceLastClick = now - lastClickRef.current;
    
    if (timeSinceLastClick < 300) {
      // Double click - navigate to canonical tab
      if (box.deep_link_tab && onNavigate) {
        onNavigate(box.deep_link_tab, box.deep_link_section);
      }
    } else {
      // Single click - toggle expand
      onToggleExpand(box.id);
    }
    
    lastClickRef.current = now;
  };
  
  const handleSave = async () => {
    if (!editValue || !onSave) return;
    setSaving(true);
    try {
      await onSave(box.id, editValue);
      onToggleExpand(null); // Collapse
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };
  
  const handleNavigate = (e) => {
    e.stopPropagation();
    if (box.deep_link_tab && onNavigate) {
      onNavigate(box.deep_link_tab, box.deep_link_section);
    }
  };
  
  useEffect(() => {
    if (isExpanded) {
      setEditValue(box.value);
    }
  }, [isExpanded, box.value]);
  
  return (
    <div 
      className={`rounded-xl p-4 cursor-pointer transition-all ${statusColors[box.status]} hover:shadow-md`}
      onClick={handleClick}
      data-testid={`command-box-${box.id}`}
    >
      {/* Collapsed State */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {getBoxIcon()}
          <div>
            <p className="text-sm font-medium text-[#1A1A1A]">{box.label}</p>
            <p className="text-xs text-[#8C8A86]">{box.summary}</p>
          </div>
        </div>
        <button 
          onClick={handleNavigate}
          className="p-1.5 rounded-lg hover:bg-white/50 text-[#8C8A86] hover:text-[#1A1A1A] transition-colors"
          title="Open in tab"
        >
          {getVeraultIcon('external', { size: 16, color: VERAULT_COLORS.stone })}
        </button>
      </div>
      
      {/* Expanded State */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pt-4 mt-4 border-t border-[rgba(0,0,0,0.06)]">
              {/* Render appropriate edit field based on box type */}
              {box.id === 'travelers' && (
                <div className="text-xs text-[#8C8A86]">
                  {Array.isArray(box.value) && box.value.length > 0 
                    ? box.value.join(', ')
                    : 'No travelers linked'}
                  <p className="mt-2 text-[#C8A85A]">Double-click to manage travelers</p>
                </div>
              )}
              
              {box.id === 'dates' && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-[#8C8A86]">Start</label>
                    <Input 
                      type="date"
                      value={editValue?.start_date || ''}
                      onChange={(e) => setEditValue({...editValue, start_date: e.target.value})}
                      className="mt-1 bg-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#8C8A86]">End</label>
                    <Input 
                      type="date"
                      value={editValue?.end_date || ''}
                      onChange={(e) => setEditValue({...editValue, end_date: e.target.value})}
                      className="mt-1 bg-white text-sm"
                    />
                  </div>
                </div>
              )}
              
              {box.id === 'destination' && (
                <DestinationSearch 
                  value={editValue || ''}
                  onChange={(val) => setEditValue(val)}
                  onSelect={(location) => {
                    setEditValue(location.display_name);
                    // Store location data for later
                    box.locationData = location;
                  }}
                />
              )}
              
              {box.id === 'estimate' && (
                <div>
                  <label className="text-xs text-[#8C8A86]">Total Price ($)</label>
                  <Input 
                    type="number"
                    value={editValue || ''}
                    onChange={(e) => setEditValue(e.target.value)}
                    placeholder="0.00"
                    className="mt-1 bg-white text-sm"
                  />
                </div>
              )}
              
              {box.id === 'weather' && (
                <div className="text-xs text-[#8C8A86]">
                  <p>Weather data is loaded from destination.</p>
                  <p className="mt-2 text-[#C8A85A]">Double-click to view full forecast in Logistics</p>
                </div>
              )}
              
              {box.id === 'countdown' && (
                <div className="text-xs text-[#8C8A86]">
                  <p>Countdown is calculated from trip dates.</p>
                  <p className="mt-2 text-[#C8A85A]">Double-click to view calendar</p>
                </div>
              )}
              
              {(box.id === 'itinerary' || box.id === 'agreement') && (
                <div className="text-xs text-[#8C8A86]">
                  <p className="text-[#C8A85A]">Double-click to open</p>
                </div>
              )}
              
              {/* Intake actions */}
              {box.id === 'intake' && (
                <IntakeActions 
                  tripId={tripId} 
                  intakeStatus={box.value}
                  actions={box.actions || []}
                  onSuccess={() => onToggleExpand(null)}
                />
              )}
              
              {/* Save button for editable fields */}
              {box.editable_fields && box.editable_fields.length > 0 && box.id !== 'travelers' && (
                <div className="flex justify-end gap-2 mt-3">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => onToggleExpand(null)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    size="sm"
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-[#C8A85A] hover:bg-[#B39049] text-white"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ==================== REVEAL CONTROLS PANEL ====================
// Centralized control panel for managing client-facing visibility per trip
function RevealControlsPanel({ tripId, form, onFormUpdate }) {
  const [saving, setSaving] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  
  const handleChange = async (field, value) => {
    if (onFormUpdate) {
      onFormUpdate({ [field]: value });
    }
    
    // Auto-save on change
    setSaving(true);
    try {
      await axios.patch(`${API}/travels/trips/${tripId}`, { [field]: value });
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };
  
  const REVEAL_OPTIONS = {
    destination: [
      { value: 'full', label: 'Show Full', description: 'Display complete destination details' },
      { value: 'masked', label: 'Surprise Mode', description: 'Replace with custom mask text' }
    ],
    dates: [
      { value: 'full', label: 'Show Full', description: 'Display exact dates' },
      { value: 'partial', label: 'Partial', description: 'Show month/season only' },
      { value: 'hidden', label: 'Hidden', description: 'Hide dates completely' }
    ],
    weather: [
      { value: 'full', label: 'Full Details', description: 'Show weather with location' },
      { value: 'abstract', label: 'Abstract Only', description: 'Conditions without location' },
      { value: 'hidden', label: 'Hidden', description: 'No weather shown' }
    ],
    itinerary: [
      { value: 'full', label: 'Full Details', description: 'Complete itinerary visible' },
      { value: 'structure', label: 'Structure Only', description: 'Day numbers only, no details' },
      { value: 'hidden', label: 'Hidden', description: 'Itinerary not visible' }
    ]
  };

  return (
    <div 
      className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] overflow-hidden"
      data-testid="reveal-controls-panel"
      id="reveal-controls"
    >
      {/* Header */}
      <div className="p-4 border-b border-[rgba(0,0,0,0.06)] bg-gradient-to-r from-[#1A1A1A] to-[#2C2C2C]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#C8A85A]/20 flex items-center justify-center">
              <Eye className="w-5 h-5 text-[#C8A85A]" />
            </div>
            <div>
              <h3 className="font-serif text-lg text-white">Reveal Controls</h3>
              <p className="text-xs text-white/60">Manage what your client sees</p>
            </div>
          </div>
          {saving && <Loader2 className="w-4 h-4 animate-spin text-[#C8A85A]" />}
        </div>
      </div>
      
      <div className="p-4 space-y-4">
        {/* Banner Personalization */}
        <div className="space-y-2">
          <label className="text-xs font-medium uppercase tracking-wide text-[#8C8A86]">
            Banner Greeting
          </label>
          <Input
            value={form.client_banner_name || ''}
            onChange={(e) => handleChange('client_banner_name', e.target.value)}
            placeholder="e.g., The Smith Family, Jonathan & Sarah"
            className="bg-[#F9F8F4] border-[#E6E4DE]"
          />
          <p className="text-[10px] text-[#8C8C8C]">
            Optional personalized greeting in the client portal status banner
          </p>
        </div>
        
        {/* Journey Overview */}
        <div className="space-y-2">
          <label className="text-xs font-medium uppercase tracking-wide text-[#8C8A86]">
            Journey Overview
          </label>
          <Textarea
            value={form.journey_overview || ''}
            onChange={(e) => handleChange('journey_overview', e.target.value)}
            placeholder="Set the emotional tone for this journey..."
            className="bg-[#F9F8F4] border-[#E6E4DE] min-h-[80px]"
            rows={3}
          />
          <p className="text-[10px] text-[#8C8C8C]">
            Appears on the Home page. Leave empty to hide this section entirely.
          </p>
        </div>
        
        <div className="border-t border-[#E6E4DE] pt-4 mt-4">
          <p className="text-xs font-medium uppercase tracking-wide text-[#8C8A86] mb-3">
            Visibility Controls
          </p>
          
          {/* Destination Visibility */}
          <div className="space-y-2 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#C8A85A]" />
                <span className="text-sm font-medium text-[#1A1A1A]">Destination</span>
              </div>
              <Select 
                value={form.reveal_destination || 'full'} 
                onValueChange={(v) => handleChange('reveal_destination', v)}
              >
                <SelectTrigger className="w-[140px] h-8 text-xs bg-[#F9F8F4]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REVEAL_OPTIONS.destination.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.reveal_destination === 'masked' && (
              <Input
                value={form.reveal_destination_mask || 'A surprise ✨'}
                onChange={(e) => handleChange('reveal_destination_mask', e.target.value)}
                placeholder="Custom mask text"
                className="bg-[#F9F8F4] border-[#E6E4DE] text-sm h-8 ml-6"
              />
            )}
          </div>
          
          {/* Dates Visibility */}
          <div className="space-y-2 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#C8A85A]" />
                <span className="text-sm font-medium text-[#1A1A1A]">Dates</span>
              </div>
              <Select 
                value={form.reveal_dates || 'full'} 
                onValueChange={(v) => handleChange('reveal_dates', v)}
              >
                <SelectTrigger className="w-[140px] h-8 text-xs bg-[#F9F8F4]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REVEAL_OPTIONS.dates.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.reveal_dates === 'partial' && (
              <Input
                value={form.reveal_dates_mask || 'Coming soon'}
                onChange={(e) => handleChange('reveal_dates_mask', e.target.value)}
                placeholder="e.g., Mid-June 2026"
                className="bg-[#F9F8F4] border-[#E6E4DE] text-sm h-8 ml-6"
              />
            )}
          </div>
          
          {/* Weather Visibility */}
          <div className="space-y-2 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Cloud className="w-4 h-4 text-[#C8A85A]" />
                <span className="text-sm font-medium text-[#1A1A1A]">Weather</span>
              </div>
              <Select 
                value={form.reveal_weather || 'full'} 
                onValueChange={(v) => handleChange('reveal_weather', v)}
              >
                <SelectTrigger className="w-[140px] h-8 text-xs bg-[#F9F8F4]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REVEAL_OPTIONS.weather.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-[10px] text-[#8C8C8C] ml-6">
              Weather is shown in Prep tab only, never on Home
            </p>
          </div>
          
          {/* Itinerary Visibility */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-[#C8A85A]" />
                <span className="text-sm font-medium text-[#1A1A1A]">Itinerary</span>
              </div>
              <Select 
                value={form.reveal_itinerary || 'full'} 
                onValueChange={(v) => handleChange('reveal_itinerary', v)}
              >
                <SelectTrigger className="w-[140px] h-8 text-xs bg-[#F9F8F4]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REVEAL_OPTIONS.itinerary.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        
        {/* Helper text */}
        <div className="bg-[#F9F8F4] rounded-lg p-3 mt-4">
          <p className="text-xs text-[#595959] leading-relaxed">
            <strong className="text-[#1A1A1A]">Tip:</strong> Changes apply instantly across all client-facing tabs. 
            The client never sees these controls — their experience reflects your choices silently.
          </p>
        </div>
      </div>
    </div>
  );
}

// ==================== COMMAND CENTER PANEL ====================
function CommandCenterPanel({ tripId, form, itinerary, onNavigate, onFormUpdate }) {
  const [lifecycle, setLifecycle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedBox, setExpandedBox] = useState(null);

  useEffect(() => {
    if (tripId) fetchLifecycle();
  }, [tripId, form]);

  const fetchLifecycle = async () => {
    try {
      const res = await axios.get(`${API}/travels/trips/${tripId}/command-center`);
      setLifecycle(res.data);
    } catch (e) {
      console.error('Failed to fetch lifecycle', e);
    } finally {
      setLoading(false);
    }
  };

  const handleAdvance = async () => {
    try {
      const res = await axios.post(`${API}/travels/trips/${tripId}/lifecycle/advance`);
      if (res.data.advanced) {
        toast.success(`Advanced to ${MASTER_LIFECYCLE[res.data.to_stage]?.label}`);
        fetchLifecycle();
      } else {
        toast.info(res.data.reason || 'Cannot advance yet');
      }
    } catch (e) {
      toast.error('Failed to advance lifecycle');
    }
  };

  const handleStageChange = async (newStage) => {
    try {
      await axios.patch(`${API}/travels/trips/${tripId}/lifecycle?stage=${newStage}`);
      toast.success(`Stage updated to ${MASTER_LIFECYCLE[newStage]?.label}`);
      fetchLifecycle();
    } catch (e) {
      toast.error('Failed to update lifecycle');
    }
  };
  
  const handleBoxSave = async (boxId, value) => {
    // Save to the canonical data source - use single source of truth
    let updatePayload = {};
    
    if (boxId === 'dates') {
      // Update both new and legacy fields for full consistency
      updatePayload = { 
        trip_start_date: value.start_date, 
        trip_end_date: value.end_date,
        start_date: value.start_date, 
        end_date: value.end_date 
      };
    } else if (boxId === 'destination') {
      updatePayload = { destinations: value };
    } else if (boxId === 'estimate') {
      updatePayload = { price_total: parseFloat(value) || 0 };
    }
    
    if (Object.keys(updatePayload).length > 0) {
      await axios.patch(`${API}/travels/trips/${tripId}`, updatePayload);
      toast.success('Saved');
      fetchLifecycle();
      if (onFormUpdate) onFormUpdate(updatePayload);
    }
  };
  
  const handleNavigate = (tab, section) => {
    if (onNavigate) {
      onNavigate(tab, section);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-[#C8A85A]" />
      </div>
    );
  }

  const currentStage = lifecycle?.lifecycle_stage || 'planning_initiated';
  const stageInfo = MASTER_LIFECYCLE[currentStage] || {};
  const currentIdx = LIFECYCLE_ORDER.indexOf(currentStage);
  const commandBoxes = lifecycle?.command_boxes || [];
  const nextAction = lifecycle?.next_required_action;
  const waitingOnParty = lifecycle?.waiting_on_party_label;

  return (
    <div className="space-y-6">
      {/* Lifecycle Stepper - Horizontal on desktop, scrollable on mobile */}
      <div className="relative overflow-x-auto pb-2 -mx-2 px-2 md:mx-0 md:px-0">
        <div className="flex items-center min-w-max gap-1 md:gap-2">
          {LIFECYCLE_ORDER.map((stage, idx) => {
            const info = MASTER_LIFECYCLE[stage];
            const isActive = stage === currentStage;
            const isPast = idx < currentIdx;
            
            return (
              <div key={stage} className="flex items-center">
                <button
                  onClick={() => handleStageChange(stage)}
                  className={`px-2 py-1.5 md:px-3 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all flex items-center gap-1 md:gap-2 ${
                    isActive 
                      ? 'bg-[#C8A85A] text-white shadow-lg' 
                      : isPast 
                        ? 'bg-[#8DA399]/20 text-[#8DA399]' 
                        : 'bg-[#E6E1DA] text-[#8C8A86] hover:bg-[#EEE9E2]'
                  }`}
                  data-testid={`lifecycle-stage-${stage}`}
                >
                  {getVeraultIcon(info.iconName, { 
                    size: 16, 
                    color: isActive ? '#FFFFFF' : isPast ? '#8DA399' : '#8C8A86' 
                  })}
                  <span className="hidden lg:inline">{info.label}</span>
                </button>
                {idx < LIFECYCLE_ORDER.length - 1 && (
                  <div className={`w-3 md:w-6 h-0.5 mx-0.5 md:mx-1 ${isPast ? 'bg-[#8DA399]' : 'bg-[#E6E1DA]'}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Status Snapshot - Current Stage + Waiting-On + Next Action */}
      <div className="p-4 md:p-5 rounded-xl bg-[#1A1A1A] text-white">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              {getVeraultIcon(stageInfo.iconName, { size: 18, color: VERAULT_COLORS.gold })}
              <span className="text-xs uppercase tracking-wide text-[#8C8A86]">Current Stage</span>
              {waitingOnParty && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-[#C8A85A]/20 text-[#C8A85A]">
                  {waitingOnParty}
                </span>
              )}
            </div>
            <h3 className="font-serif text-xl md:text-2xl">{stageInfo.label}</h3>
            <p className="text-sm text-white/60 mt-1">{stageInfo.story}</p>
          </div>
          
          {/* Next Required Action */}
          {nextAction && (
            <div className="p-3 bg-white/5 rounded-lg md:max-w-xs">
              <p className="text-xs uppercase tracking-wide text-[#C8A85A] mb-1">Next Required Action</p>
              <p className="text-sm text-white/90">{nextAction.label}</p>
            </div>
          )}
          
          {lifecycle?.can_advance && (
            <Button
              onClick={handleAdvance}
              className="bg-[#C8A85A] hover:bg-[#B39049] text-white self-start md:self-center"
              data-testid="advance-lifecycle-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              Advance
            </Button>
          )}
        </div>
      </div>

      {/* Command Boxes Grid - Uniform card-style */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        {commandBoxes.map((box) => (
          <CommandBox
            key={box.id}
            box={box}
            tripId={tripId}
            form={form}
            onNavigate={handleNavigate}
            onSave={handleBoxSave}
            isExpanded={expandedBox === box.id}
            onToggleExpand={setExpandedBox}
          />
        ))}
        
        {/* Forms Panel - integrated in grid */}
        <TripFormsPanel 
          tripId={tripId} 
          clientId={form?.traveler_profile_ids?.[0]}
        />
      </div>
      
      {/* Reveal Controls Panel - Client visibility management */}
      <RevealControlsPanel 
        tripId={tripId}
        form={form}
        onFormUpdate={onFormUpdate}
      />

      {/* Trip Timeline - Aggregated milestones from system data */}
      <TripTimeline tripId={tripId} />
    </div>
  );
}

// ==================== TRAVELER PROFILE MANAGER ====================
function TravelerProfileManager({ tripId, form, onTravelersChange }) {
  const [travelers, setTravelers] = useState([]);
  const [allTravelers, setAllTravelers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newTraveler, setNewTraveler] = useState({ full_name: '', email: '', phone: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    if (tripId) fetchTravelers();
  }, [tripId]);

  const fetchTravelers = async () => {
    try {
      const res = await axios.get(`${API}/travels/trips/${tripId}/travelers`);
      setTravelers(res.data.travelers || []);
    } catch (e) {
      console.error('Failed to fetch travelers');
    } finally {
      setLoading(false);
    }
  };

  const searchTravelers = async (query) => {
    if (!query || query.length < 2) {
      setAllTravelers([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/travelers?search=${encodeURIComponent(query)}`);
      setAllTravelers(res.data || []);
    } catch (e) {
      console.error('Failed to search travelers');
    }
  };

  const handleCreateTraveler = async () => {
    if (!newTraveler.full_name.trim()) {
      toast.error('Full name is required');
      return;
    }
    try {
      const res = await axios.post(`${API}/travelers`, {
        ...newTraveler,
        household_client_id: form?.client_id
      });
      // Link to trip
      await axios.post(`${API}/travels/trips/${tripId}/link-traveler?traveler_id=${res.data.id}`);
      toast.success('Traveler created and linked');
      setNewTraveler({ full_name: '', email: '', phone: '' });
      setShowAdd(false);
      fetchTravelers();
      if (onTravelersChange) onTravelersChange();
    } catch (e) {
      toast.error('Failed to create traveler');
    }
  };

  const handleLinkTraveler = async (travelerId) => {
    try {
      await axios.post(`${API}/travels/trips/${tripId}/link-traveler?traveler_id=${travelerId}`);
      toast.success('Traveler linked');
      setShowSearch(false);
      setSearchQuery('');
      fetchTravelers();
      if (onTravelersChange) onTravelersChange();
    } catch (e) {
      toast.error('Failed to link traveler');
    }
  };

  const handleUnlinkTraveler = async (travelerId) => {
    try {
      await axios.post(`${API}/travels/trips/${tripId}/unlink-traveler?traveler_id=${travelerId}`);
      toast.success('Traveler unlinked');
      fetchTravelers();
      if (onTravelersChange) onTravelersChange();
    } catch (e) {
      toast.error('Failed to unlink traveler');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-[#C8A85A]" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* Linked Travelers */}
      <div className="space-y-2">
        {travelers.length === 0 ? (
          <p className="text-sm text-[#8C8C8C] py-2">No travelers linked to this trip yet</p>
        ) : (
          travelers.map((t) => (
            <div key={t.id} className="flex items-center justify-between p-3 bg-[#EEE9E2] rounded-lg">
              <div>
                <p className="font-medium text-[#1A1A1A]">{t.full_name}</p>
                <p className="text-xs text-[#8C8C8C]">{[t.email, t.phone].filter(Boolean).join(' • ') || 'No contact info'}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => handleUnlinkTraveler(t.id)} className="text-[#B37E6B] hover:text-[#B37E6B] hover:bg-[#B37E6B]/10">
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={() => setShowAdd(!showAdd)} className="text-[#C8A85A] border-[#C8A85A]">
          <Plus className="w-4 h-4 mr-1" /> New Traveler
        </Button>
        <Button variant="outline" size="sm" onClick={() => setShowSearch(!showSearch)} className="text-[#8C8C8C]">
          <Users className="w-4 h-4 mr-1" /> Link Existing
        </Button>
      </div>

      {/* Add New Traveler Form */}
      {showAdd && (
        <div className="p-4 border border-[rgba(0,0,0,0.06)] rounded-lg space-y-3 bg-[#F5F1EB]">
          <p className="text-sm font-medium text-[#1A1A1A]">Create New Traveler</p>
          <Input 
            placeholder="Full Name *" 
            value={newTraveler.full_name} 
            onChange={(e) => setNewTraveler({...newTraveler, full_name: e.target.value})}
            className="bg-white"
          />
          <div className="grid grid-cols-2 gap-2">
            <Input 
              placeholder="Email" 
              type="email"
              value={newTraveler.email} 
              onChange={(e) => setNewTraveler({...newTraveler, email: e.target.value})}
              className="bg-white"
            />
            <Input 
              placeholder="Phone" 
              value={newTraveler.phone} 
              onChange={(e) => setNewTraveler({...newTraveler, phone: e.target.value})}
              className="bg-white"
            />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleCreateTraveler} className="bg-[#C8A85A] hover:bg-[#B39049] text-white">
              Create & Link
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowAdd(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Search Existing Travelers */}
      {showSearch && (
        <div className="p-4 border border-[rgba(0,0,0,0.06)] rounded-lg space-y-3 bg-[#F5F1EB]">
          <p className="text-sm font-medium text-[#1A1A1A]">Link Existing Traveler</p>
          <Input 
            placeholder="Search by name..." 
            value={searchQuery} 
            onChange={(e) => {
              setSearchQuery(e.target.value);
              searchTravelers(e.target.value);
            }}
            className="bg-white"
          />
          {allTravelers.length > 0 && (
            <div className="max-h-40 overflow-y-auto space-y-1">
              {allTravelers
                .filter(t => !travelers.find(linked => linked.id === t.id))
                .map((t) => (
                <button
                  key={t.id}
                  onClick={() => handleLinkTraveler(t.id)}
                  className="w-full text-left p-2 rounded hover:bg-white transition-colors"
                >
                  <p className="text-sm font-medium text-[#1A1A1A]">{t.full_name}</p>
                  <p className="text-xs text-[#8C8C8C]">{t.email}</p>
                </button>
              ))}
            </div>
          )}
          <Button variant="ghost" size="sm" onClick={() => { setShowSearch(false); setSearchQuery(''); }}>Close</Button>
        </div>
      )}
    </div>
  );
}

// ==================== POST-TRIP EMAIL PANEL ====================
function PostTripEmailPanel({ tripId, form }) {
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');

  // Determine if trip is in a phase where post-trip emails are relevant
  const lifecycleStage = form?.lifecycle_stage || 'planning_initiated';
  const isRelevantStage = ['trip_confirmed', 'in_travel', 'completed'].includes(lifecycleStage);

  useEffect(() => {
    if (tripId && isRelevantStage) fetchEmailLogs();
  }, [tripId, isRelevantStage]);

  const fetchEmailLogs = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/travels/trips/${tripId}/email-logs`);
      setEmailLogs(res.data || []);
    } catch (e) {
      console.error('Failed to fetch email logs');
    } finally {
      setLoading(false);
    }
  };

  const sendWelcomeHomeEmail = async () => {
    try {
      setSending(true);
      const url = recipientEmail 
        ? `${API}/travels/trips/${tripId}/send-welcome-home-email?recipient_email=${encodeURIComponent(recipientEmail)}`
        : `${API}/travels/trips/${tripId}/send-welcome-home-email`;
      const res = await axios.post(url);
      if (res.data.success) {
        toast.success('Welcome home email sent!');
        fetchEmailLogs();
      } else {
        toast.info(res.data.reason || 'Email not sent');
      }
    } catch (e) {
      toast.error('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const sendFeedbackEmail = async () => {
    try {
      setSending(true);
      const url = recipientEmail 
        ? `${API}/travels/trips/${tripId}/send-feedback-request-email?recipient_email=${encodeURIComponent(recipientEmail)}`
        : `${API}/travels/trips/${tripId}/send-feedback-request-email`;
      const res = await axios.post(url);
      if (res.data.success) {
        toast.success('Feedback request email sent!');
        fetchEmailLogs();
      } else {
        toast.info(res.data.reason || 'Email not sent');
      }
    } catch (e) {
      toast.error('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  // Don't render if trip isn't in relevant stage
  if (!isRelevantStage) return null;

  return (
    <div className="bg-white rounded-xl p-6 border border-[rgba(0,0,0,0.06)]">
      <h2 className="font-serif text-lg text-[#1A1A1A] mb-4 flex items-center gap-2">
        <Send className="w-5 h-5 text-[#C8A85A]" /> Post-Trip Emails
      </h2>
      
      <p className="text-xs text-[#8C8C8C] mb-4">
        Send curated follow-up emails to clients after their journey. Emails are automatically triggered when a trip is marked as "Completed", or you can send manually below.
      </p>

      {/* Recipient Override */}
      <div className="mb-4">
        <label className="text-xs text-[#8C8C8C] uppercase tracking-wide">Recipient Email (Optional Override)</label>
        <Input 
          type="email"
          placeholder="Uses client email if empty"
          value={recipientEmail}
          onChange={(e) => setRecipientEmail(e.target.value)}
          className="mt-1 bg-[#EEE9E2]"
        />
      </div>

      {/* Email Actions */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={sendWelcomeHomeEmail}
          disabled={sending}
          className="border-[#8DA399] text-[#8DA399]"
        >
          {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Heart className="w-4 h-4 mr-1" />}
          Welcome Home
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={sendFeedbackEmail}
          disabled={sending}
          className="border-[#C8A85A] text-[#C8A85A]"
        >
          {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-1" />}
          Feedback Request
        </Button>
      </div>

      {/* Email Log */}
      {emailLogs.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-[#8C8C8C]">Email History</p>
          {emailLogs.slice(0, 5).map((log) => (
            <div key={log.id} className="flex items-center justify-between p-2 bg-[#EEE9E2] rounded-lg text-sm">
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${log.status === 'sent' ? 'bg-[#8DA399]' : log.status === 'failed' ? 'bg-[#B37E6B]' : 'bg-[#C8A85A]'}`} />
                <span className="font-medium text-[#1A1A1A]">{log.email_type?.replace('_', ' ')}</span>
              </div>
              <span className="text-xs text-[#8C8C8C]">
                {log.sent_at ? formatDate(log.sent_at) : log.status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Workflow Stage Panel Component
function WorkflowStagePanel({ tripId, currentStage, clientVisibleTabs, onStageChange, onTabsChange }) {
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [workflowHistory, setWorkflowHistory] = useState([]);

  const handleStageChange = async (newStage) => {
    if (!tripId || loading) return;
    setLoading(true);
    try {
      const res = await axios.patch(`${API}/travels/trips/${tripId}/workflow-stage?stage=${newStage}`);
      onStageChange(newStage);
      toast.success(`Stage updated to "${WORKFLOW_STAGES[newStage]?.label}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update stage');
    } finally {
      setLoading(false);
    }
  };

  const handleTabToggle = async (tabId) => {
    if (!tripId) return;
    const newTabs = clientVisibleTabs.includes(tabId)
      ? clientVisibleTabs.filter(t => t !== tabId)
      : [...clientVisibleTabs, tabId];
    
    try {
      await axios.patch(`${API}/travels/trips/${tripId}/client-visible-tabs`, newTabs);
      onTabsChange(newTabs);
    } catch (e) {
      toast.error('Failed to update client tabs');
    }
  };

  const fetchHistory = async () => {
    if (!tripId) return;
    try {
      const res = await axios.get(`${API}/travels/trips/${tripId}/workflow-status`);
      setWorkflowHistory(res.data.workflow_history || []);
    } catch (e) {
      console.error('Failed to fetch workflow history');
    }
  };

  useEffect(() => {
    if (showHistory && tripId) fetchHistory();
  }, [showHistory, tripId]);

  const currentIndex = WORKFLOW_STAGE_ORDER.indexOf(currentStage);

  return (
    <div className="space-y-6">
      {/* Stepper Visualization */}
      <div className="relative">
        <div className="flex items-center justify-between">
          {WORKFLOW_STAGE_ORDER.map((stage, idx) => {
            const stageInfo = WORKFLOW_STAGES[stage];
            const isActive = stage === currentStage;
            const isPast = idx < currentIndex;
            
            return (
              <div key={stage} className="flex flex-col items-center relative" style={{ flex: 1 }}>
                {/* Connector line */}
                {idx < WORKFLOW_STAGE_ORDER.length - 1 && (
                  <div 
                    className={`absolute top-4 left-1/2 w-full h-0.5 ${isPast || isActive ? 'bg-[#C8A85A]' : 'bg-[#E6E4DE]'}`}
                    style={{ zIndex: 0 }}
                  />
                )}
                
                {/* Step circle */}
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm z-10 transition-all ${
                    isActive 
                      ? `${stageInfo.color} text-white ring-2 ring-offset-2 ring-[#C8A85A]` 
                      : isPast 
                        ? 'bg-[#C8A85A] text-white' 
                        : 'bg-[#E6E4DE] text-[#8C8C8C]'
                  }`}
                >
                  {isPast ? <Check className="w-4 h-4" /> : stageInfo.icon}
                </div>
                
                {/* Label - only show for active and adjacent */}
                {(isActive || idx === currentIndex - 1 || idx === currentIndex + 1) && (
                  <span className={`text-xs mt-1 text-center max-w-[80px] ${isActive ? 'text-[#1A1A1A] font-medium' : 'text-[#8C8C8C]'}`}>
                    {stageInfo.label}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Stage Dropdown Control */}
      <div>
        <label className="text-xs text-[#8C8C8C] uppercase tracking-wide block mb-2">Current Workflow Stage</label>
        <Select value={currentStage} onValueChange={handleStageChange} disabled={loading}>
          <SelectTrigger className="bg-[#EEE9E2]" data-testid="workflow-stage-select">
            <SelectValue>
              {WORKFLOW_STAGES[currentStage]?.icon} {WORKFLOW_STAGES[currentStage]?.label}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {WORKFLOW_STAGE_ORDER.map((stage) => {
              const info = WORKFLOW_STAGES[stage];
              return (
                <SelectItem key={stage} value={stage}>
                  <span className="flex items-center gap-2">
                    <span>{info.icon}</span>
                    <span>{info.label}</span>
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        <p className="text-xs text-[#8C8C8C] mt-1">
          You can move to any stage — the workflow guides but doesn't enforce.
        </p>
      </div>

      {/* Client Visible Tabs Control */}
      <div>
        <label className="text-xs text-[#8C8C8C] uppercase tracking-wide block mb-2">Member Suite Tabs</label>
        <p className="text-xs text-[#B0B0B0] mb-3">Toggle which tabs the client can see in their Member Suite</p>
        <div className="flex flex-wrap gap-2">
          {CLIENT_TAB_OPTIONS.map((tab) => {
            const isVisible = clientVisibleTabs.includes(tab.id);
            return (
              <button
                key={tab.id}
                onClick={() => handleTabToggle(tab.id)}
                className={`px-3 py-1.5 rounded-lg text-sm border transition-all ${
                  isVisible 
                    ? 'bg-[#C8A85A]/10 border-[#C8A85A] text-[#C8A85A]' 
                    : 'bg-[#F5F5F5] border-[rgba(0,0,0,0.06)] text-[#8C8C8C]'
                }`}
                data-testid={`client-tab-toggle-${tab.id}`}
              >
                {isVisible ? <Eye className="w-3 h-3 inline mr-1" /> : <EyeOff className="w-3 h-3 inline mr-1" />}
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* History Toggle */}
      <div className="border-t border-[rgba(0,0,0,0.06)] pt-4">
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="text-xs text-[#C8A85A] hover:underline flex items-center gap-1"
        >
          {showHistory ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {showHistory ? 'Hide' : 'Show'} Stage History
        </button>
        
        {showHistory && workflowHistory.length > 0 && (
          <div className="mt-3 space-y-2 max-h-40 overflow-y-auto">
            {workflowHistory.slice().reverse().map((entry, idx) => (
              <div key={idx} className="text-xs text-[#595959] flex items-center gap-2 p-2 bg-[#EEE9E2] rounded">
                <span className="text-[#8C8C8C]">{formatDate(entry.timestamp)}</span>
                <span>{WORKFLOW_STAGES[entry.from_stage]?.icon} → {WORKFLOW_STAGES[entry.to_stage]?.icon}</span>
                <span className="font-medium">{WORKFLOW_STAGES[entry.to_stage]?.label}</span>
                {entry.notes && <span className="text-[#B0B0B0]">({entry.notes})</span>}
              </div>
            ))}
          </div>
        )}
        {showHistory && workflowHistory.length === 0 && (
          <p className="text-xs text-[#8C8C8C] mt-2">No stage changes recorded yet</p>
        )}
      </div>
    </div>
  );
}

// Trip Phase Configuration (Client-facing status)
const TRIP_PHASES = {
  agreements_pending: { label: 'Agreements Pending', icon: '📝', default_banner: 'Please sign your agreement to continue' },
  deposit_received: { label: 'Deposit Received', icon: '✅', default_banner: 'Thank you — your deposit has been received' },
  itinerary_drafting: { label: 'Itinerary Drafting', icon: '✨', default_banner: 'Your personalized itinerary is being crafted' },
  itinerary_sent: { label: 'Itinerary Sent', icon: '📤', default_banner: 'Your itinerary is ready for review' },
  client_revisions: { label: 'Client Revisions', icon: '🔄', default_banner: "We're incorporating your feedback" },
  itinerary_approved: { label: 'Itinerary Approved', icon: '👍', default_banner: 'Wonderful! Your itinerary has been approved' },
  payment_authorized: { label: 'Payment Authorized', icon: '💳', default_banner: 'Payment received — securing your reservations' },
  booking_in_progress: { label: 'Booking In Progress', icon: '⏳', default_banner: 'Your reservations are being confirmed' },
  booked: { label: 'Booked', icon: '✈️', default_banner: 'All bookings complete — your countdown has begun' },
  pre_trip: { label: 'Pre-Trip', icon: '🎒', default_banner: 'Final preparations underway — your adventure awaits' },
  on_trip: { label: 'On Trip', icon: '🌍', default_banner: 'Have an extraordinary journey' },
  completed: { label: 'Completed', icon: '🏆', default_banner: 'Welcome home — we hope it was unforgettable' },
};

const TRIP_PHASE_ORDER = [
  'agreements_pending', 'deposit_received', 'itinerary_drafting', 'itinerary_sent',
  'client_revisions', 'itinerary_approved', 'payment_authorized', 'booking_in_progress',
  'booked', 'pre_trip', 'on_trip', 'completed'
];

const BANNER_ICON_OPTIONS = [
  { value: 'sparkles', label: '✨ Sparkles' },
  { value: 'bell', label: '🔔 Bell' },
  { value: 'calendar', label: '📅 Calendar' },
  { value: 'credit_card', label: '💳 Card' },
  { value: 'plane', label: '✈️ Plane' },
  { value: 'check', label: '✅ Check' },
  { value: 'alert', label: '⚠️ Alert' },
  { value: 'file_text', label: '📄 Document' },
  { value: 'clock', label: '⏰ Clock' },
  { value: 'edit', label: '✏️ Edit' },
  { value: 'heart', label: '❤️ Heart' },
];

// Trip Phase & Banner Panel Component
function TripPhasePanel({ tripId, currentPhase, onPhaseChange }) {
  const [loading, setLoading] = useState(false);
  const [showBannerForm, setShowBannerForm] = useState(false);
  const [bannerMessage, setBannerMessage] = useState('');
  const [bannerIcon, setBannerIcon] = useState('sparkles');
  const [bannerExpires, setBannerExpires] = useState('');
  const [bannerActionUrl, setBannerActionUrl] = useState('');
  const [bannerActionLabel, setBannerActionLabel] = useState('');
  const [currentBanner, setCurrentBanner] = useState(null);

  useEffect(() => {
    if (tripId) fetchBannerStatus();
  }, [tripId]);

  const fetchBannerStatus = async () => {
    try {
      const res = await axios.get(`${API}/travels/trips/${tripId}/banner-status`);
      setCurrentBanner(res.data);
      // Always populate form fields from server state for consistency
      if (res.data.custom_banner_active) {
        setBannerMessage(res.data.custom_banner || '');
        setBannerIcon(res.data.effective_icon || 'sparkles');
        setBannerExpires(res.data.banner_expires_at || '');
        setBannerActionUrl(res.data.banner_action_url || '');
        setBannerActionLabel(res.data.banner_action_label || '');
      } else {
        // Clear form if no custom banner is active
        setBannerMessage('');
        setBannerIcon('sparkles');
        setBannerExpires('');
        setBannerActionUrl('');
        setBannerActionLabel('');
      }
    } catch (e) {
      console.error('Failed to fetch banner status');
    }
  };

  const handlePhaseChange = async (newPhase) => {
    if (!tripId || loading) return;
    setLoading(true);
    try {
      await axios.patch(`${API}/travels/trips/${tripId}/trip-phase?phase=${newPhase}`);
      onPhaseChange(newPhase);
      fetchBannerStatus();
      toast.success(`Trip phase updated to "${TRIP_PHASES[newPhase]?.label}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update phase');
    } finally {
      setLoading(false);
    }
  };

  const handleSendBanner = async () => {
    if (!tripId || !bannerMessage.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/travels/trips/${tripId}/send-banner`, {
        message: bannerMessage.trim(),
        icon: bannerIcon,
        expires_at: bannerExpires || null,
        action_url: bannerActionUrl || null,
        action_label: bannerActionLabel || null
      });
      toast.success('Banner message sent to client portal');
      setShowBannerForm(false);
      // Don't clear fields - fetchBannerStatus will repopulate from server
      await fetchBannerStatus();
    } catch (e) {
      toast.error('Failed to send banner');
    } finally {
      setLoading(false);
    }
  };

  const handleClearBanner = async () => {
    if (!tripId) return;
    try {
      await axios.delete(`${API}/travels/trips/${tripId}/banner`);
      toast.success('Custom banner cleared');
      fetchBannerStatus();
    } catch (e) {
      toast.error('Failed to clear banner');
    }
  };

  const currentIndex = TRIP_PHASE_ORDER.indexOf(currentPhase);

  return (
    <div className="space-y-6">
      {/* Phase Stepper - Domino-style tracker */}
      <div className="relative overflow-x-auto pb-2">
        <div className="flex items-center min-w-max gap-1">
          {TRIP_PHASE_ORDER.map((phase, idx) => {
            const info = TRIP_PHASES[phase];
            const isActive = phase === currentPhase;
            const isPast = idx < currentIndex;
            
            return (
              <div key={phase} className="flex items-center">
                <div 
                  className={`px-2 py-1 rounded text-xs font-medium transition-all cursor-pointer hover:scale-105 ${
                    isActive 
                      ? 'bg-[#C8A85A] text-white shadow-lg' 
                      : isPast 
                        ? 'bg-[#8DA399]/20 text-[#8DA399]' 
                        : 'bg-[#F5F5F5] text-[#8C8C8C]'
                  }`}
                  onClick={() => handlePhaseChange(phase)}
                  title={info.label}
                >
                  {info.icon}
                </div>
                {idx < TRIP_PHASE_ORDER.length - 1 && (
                  <div className={`w-4 h-0.5 ${isPast ? 'bg-[#8DA399]' : 'bg-[#E6E4DE]'}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Phase Dropdown */}
      <div>
        <label className="text-xs text-[#8C8C8C] uppercase tracking-wide block mb-2">Client Status</label>
        <Select value={currentPhase} onValueChange={handlePhaseChange} disabled={loading}>
          <SelectTrigger className="bg-[#EEE9E2]" data-testid="trip-phase-select">
            <SelectValue>
              {TRIP_PHASES[currentPhase]?.icon} {TRIP_PHASES[currentPhase]?.label}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {TRIP_PHASE_ORDER.map((phase) => {
              const info = TRIP_PHASES[phase];
              return (
                <SelectItem key={phase} value={phase}>
                  <span className="flex items-center gap-2">
                    <span>{info.icon}</span>
                    <span>{info.label}</span>
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        <p className="text-xs text-[#B0B0B0] mt-1">
          Default banner: "{TRIP_PHASES[currentPhase]?.default_banner}"
        </p>
      </div>

      {/* Current Banner Status */}
      {currentBanner && (
        <div className="p-3 bg-[#EEE9E2] rounded-lg border border-[rgba(0,0,0,0.06)]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-[#8C8C8C] uppercase tracking-wide">Live Banner Preview</span>
            {currentBanner.custom_banner_active && (
              <button onClick={handleClearBanner} className="text-xs text-[#B37E6B] hover:underline">
                Clear custom
              </button>
            )}
          </div>
          <div className="flex items-start gap-2">
            <span className="pt-1">{currentBanner.custom_banner_active ? '🎯' : '📋'}</span>
            <p className="text-sm text-[#1A1A1A] whitespace-pre-line">{currentBanner.effective_banner}</p>
          </div>
          {currentBanner.banner_expires_at && (
            <p className="text-xs text-[#8C8C8C] mt-1">
              Expires: {formatDateTime(currentBanner.banner_expires_at)}
            </p>
          )}
        </div>
      )}

      {/* Send Custom Banner */}
      <div className="border-t border-[rgba(0,0,0,0.06)] pt-4">
        <button
          onClick={() => setShowBannerForm(!showBannerForm)}
          className="text-sm text-[#C8A85A] hover:underline flex items-center gap-1"
        >
          {showBannerForm ? <ChevronUp className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />}
          {showBannerForm ? 'Hide' : 'Send Custom Banner Message'}
        </button>

        {showBannerForm && (
          <div className="mt-4 space-y-3 p-4 bg-[#EEE9E2] rounded-lg">
            <div>
              <label className="text-xs text-[#8C8C8C] block mb-1">Message</label>
              <Textarea
                value={bannerMessage}
                onChange={(e) => setBannerMessage(e.target.value)}
                placeholder="e.g., Payment due by March 18"
                className="min-h-[60px]"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#8C8C8C] block mb-1">Icon</label>
                <Select value={bannerIcon} onValueChange={setBannerIcon}>
                  <SelectTrigger className="bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BANNER_ICON_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#8C8C8C] block mb-1">Expires (optional)</label>
                <Input
                  type="datetime-local"
                  value={bannerExpires}
                  onChange={(e) => setBannerExpires(e.target.value)}
                  className="bg-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#8C8C8C] block mb-1">Action URL (optional)</label>
                <Input
                  value={bannerActionUrl}
                  onChange={(e) => setBannerActionUrl(e.target.value)}
                  placeholder="https://..."
                  className="bg-white"
                />
              </div>
              <div>
                <label className="text-xs text-[#8C8C8C] block mb-1">Action Label (optional)</label>
                <Input
                  value={bannerActionLabel}
                  onChange={(e) => setBannerActionLabel(e.target.value)}
                  placeholder="e.g., Pay Now"
                  className="bg-white"
                />
              </div>
            </div>
            <Button
              onClick={handleSendBanner}
              disabled={!bannerMessage.trim() || loading}
              className="bg-[#C8A85A] hover:bg-[#B39049] text-white w-full"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send to Client Portal'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

const TRIP_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', color: 'bg-[#8C8C8C]/10 text-[#8C8C8C]' },
  { value: 'changes_requested', label: 'Changes Requested', color: 'bg-[#B37E6B]/10 text-[#B37E6B]' },
  { value: 'approved', label: 'Approved', color: 'bg-[#C8A85A]/10 text-[#C8A85A]' },
  { value: 'booking_in_progress', label: 'Booking In Progress', color: 'bg-[#8DA399]/10 text-[#8DA399]' },
  { value: 'booked', label: 'Booked', color: 'bg-[#2C2C2C]/10 text-[#2C2C2C]' },
];

const CONTRACT_STATUS_OPTIONS = [
  { value: 'not_sent', label: 'Not Sent' },
  { value: 'sent', label: 'Sent' },
  { value: 'signed', label: 'Signed' },
];

const CARD_AUTH_STATUS_OPTIONS = [
  { value: 'not_sent', label: 'Not Sent' },
  { value: 'sent', label: 'Sent' },
  { value: 'received', label: 'Received' },
];

export function TripDetail({ trip: initialTrip, clients, onBack, onSave }) {
  // Current trip state (for ID tracking after creation)
  const [tripId, setTripId] = useState(initialTrip?.id || null);
  
  // Trip Overview state
  const [form, setForm] = useState({
    client_id: '',
    trip_title: '',
    trip_summary: '',
    // New date fields (DATE ONLY - no timezone shifting)
    trip_start_date: '',
    trip_end_date: '',
    // Legacy date fields (for backward compat)
    destinations: '',
    start_date: '',
    end_date: '',
    // Destination segments (multi-destination support)
    destination_segments: [],
    travel_day_weather_preview: false,
    num_travelers: 1,
    travel_styles: [],
    trip_types: [],
    budget_notes: '',
    constraints: '',
    hero_image_url: '',
    key_highlights: [],
    consult_summary: '',
    transcript_paste: '',
    key_decisions: '',
    intro_note: '',
    welcome_message: '',
    client_chat_enabled: false,
    // Lodging
    hotel_name: '',
    hotel_url: '',
    hotel_address: '',
    hotel_checkin: '',
    hotel_checkout: '',
    hotel_confirmation: '',
    hotel_notes: '',
    // Arrival Flight
    arrival_airline: '',
    arrival_flight_number: '',
    arrival_departure_airport: '',
    arrival_departure_time: '',
    arrival_arrival_airport: '',
    arrival_arrival_time: '',
    arrival_pnr: '',
    arrival_url: '',
    arrival_confirmation: '',
    arrival_notes: '',
    include_arrival_in_itinerary: true,
    // Departure Flight
    departure_airline: '',
    departure_flight_number: '',
    departure_departure_airport: '',
    departure_departure_time: '',
    departure_arrival_airport: '',
    departure_arrival_time: '',
    departure_pnr: '',
    departure_url: '',
    departure_confirmation: '',
    departure_notes: '',
    include_departure_in_itinerary: true,
    // Trip Status & Agreements
    trip_status: 'draft',
    approved_at: '',
    approval_reset_reason: '',
    contract_link: '',
    contract_status: 'not_sent',
    contract_signed_at: '',
    terms_link: '',
    terms_acknowledged: false,
    card_auth_link: '',
    card_auth_status: 'not_sent',
    // Pricing
    price_currency: 'USD',
    price_line_items: [],
    price_subtotal: 0,
    price_taxes_fees: 0,
    price_total: 0,
    // Deposit tracking
    price_deposit_amount: 0,
    price_deposit_due_date: '',
    price_deposit_received_date: '',
    price_deposit_status: 'not_paid',
    // Installment plan
    price_installments: [],
    price_payment_status: 'not_collected',
    price_notes: '',
    // Legacy
    price_deposit_required: 0,
    // International trip flag
    is_international: false,
    // Advisor Notes
    advisor_notes: '',
    client_preferences: '',
    vendor_contacts: '',
    // Client Portal Customization
    client_banner_name: '',
    journey_overview: '',
    // Reveal Controls
    reveal_destination: 'full',
    reveal_destination_mask: 'A surprise ✨',
    reveal_dates: 'full',
    reveal_dates_mask: 'Coming soon',
    reveal_weather: 'full',
    reveal_itinerary: 'full',
    reveal_documents_hidden_ids: [],
  });
  
  const [itinerary, setItinerary] = useState([]);
  const [activities, setActivities] = useState({});
  const [internalMessages, setInternalMessages] = useState([]);
  const [clientChatMessages, setClientChatMessages] = useState([]);
  const [newInternalMessage, setNewInternalMessage] = useState('');
  const [newClientMessage, setNewClientMessage] = useState('');
  const [editingMsgId, setEditingMsgId] = useState(null);
  const [editingMsgText, setEditingMsgText] = useState('');
  const [changeRequests, setChangeRequests] = useState([]);
  const [confirmations, setConfirmations] = useState([]);
  const [tripTravelers, setTripTravelers] = useState([]);
  
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingJourneyOverview, setGeneratingJourneyOverview] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishId, setPublishId] = useState(null);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const [activeSection, setActiveSection] = useState('command_center');
  const [expandedDays, setExpandedDays] = useState({});
  
  // AI Assistant state
  const [aiChat, setAiChat] = useState([]);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  
  // Copy link feedback
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Auto-save state
  const [autoSaving, setAutoSaving] = useState(false);
  const autoSaveTimerRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  
  // Notify client state
  const [notifyingClient, setNotifyingClient] = useState(false);
  
  // Magic Link state
  const [clientPortalToken, setClientPortalToken] = useState(null);
  const [clientPortalEnabled, setClientPortalEnabled] = useState(true);
  const [regeneratingToken, setRegeneratingToken] = useState(false);

  // Workflow Stage state
  const [workflowStage, setWorkflowStage] = useState('consultation_complete');
  const [clientVisibleTabs, setClientVisibleTabs] = useState(['itinerary', 'estimate', 'prep']);

  // Trip Phase state (client-facing status)
  const [tripPhase, setTripPhase] = useState('agreements_pending');
  
  // Planning State for AI day-scoped planning
  const [planningState, setPlanningState] = useState({
    current_working_day: 1,
    total_days: 0,
    days_completed: [],
    locked_days: {}
  });
  
  // Republish confirmation dialog state
  const [showRepublishConfirm, setShowRepublishConfirm] = useState(false);

  // Load trip data
  useEffect(() => {
    if (initialTrip) {
      setTripId(initialTrip.id || null);
      setForm({
        client_id: initialTrip.client_id || '',
        trip_title: initialTrip.trip_title || '',
        trip_summary: initialTrip.trip_summary || '',
        // New date fields (prefer these, fall back to legacy)
        trip_start_date: initialTrip.trip_start_date || (initialTrip.start_date ? initialTrip.start_date.slice(0, 10) : ''),
        trip_end_date: initialTrip.trip_end_date || (initialTrip.end_date ? initialTrip.end_date.slice(0, 10) : ''),
        // Legacy fields
        destinations: initialTrip.destinations || '',
        start_date: initialTrip.start_date || '',
        end_date: initialTrip.end_date || '',
        // Destination segments
        destination_segments: initialTrip.destination_segments || [],
        travel_day_weather_preview: initialTrip.travel_day_weather_preview || false,
        num_travelers: initialTrip.num_travelers || 1,
        travel_styles: initialTrip.travel_styles || [],
        trip_types: initialTrip.trip_types || [],
        budget_notes: initialTrip.budget_notes || '',
        constraints: initialTrip.constraints || initialTrip.dietary_constraints || '',
        hero_image_url: initialTrip.hero_image_url || '',
        key_highlights: initialTrip.key_highlights || [],
        consult_summary: initialTrip.consult_summary || '',
        transcript_paste: initialTrip.transcript_paste || '',
        key_decisions: initialTrip.key_decisions || '',
        intro_note: initialTrip.intro_note || '',
        welcome_message: initialTrip.welcome_message || '',
        client_chat_enabled: initialTrip.client_chat_enabled || false,
        // Lodging
        hotel_name: initialTrip.hotel_name || '',
        hotel_url: initialTrip.hotel_url || '',
        hotel_address: initialTrip.hotel_address || '',
        hotel_checkin: initialTrip.hotel_checkin || '',
        hotel_checkout: initialTrip.hotel_checkout || '',
        hotel_confirmation: initialTrip.hotel_confirmation || '',
        hotel_notes: initialTrip.hotel_notes || '',
        // Arrival Flight
        arrival_airline: initialTrip.arrival_airline || '',
        arrival_flight_number: initialTrip.arrival_flight_number || '',
        arrival_departure_airport: initialTrip.arrival_departure_airport || '',
        arrival_departure_time: initialTrip.arrival_departure_time || '',
        arrival_arrival_airport: initialTrip.arrival_arrival_airport || '',
        arrival_arrival_time: initialTrip.arrival_arrival_time || '',
        arrival_pnr: initialTrip.arrival_pnr || '',
        arrival_url: initialTrip.arrival_url || '',
        arrival_confirmation: initialTrip.arrival_confirmation || '',
        arrival_notes: initialTrip.arrival_notes || '',
        include_arrival_in_itinerary: initialTrip.include_arrival_in_itinerary !== false,
        // Departure Flight
        departure_airline: initialTrip.departure_airline || '',
        departure_flight_number: initialTrip.departure_flight_number || '',
        departure_departure_airport: initialTrip.departure_departure_airport || '',
        departure_departure_time: initialTrip.departure_departure_time || '',
        departure_arrival_airport: initialTrip.departure_arrival_airport || '',
        departure_arrival_time: initialTrip.departure_arrival_time || '',
        departure_pnr: initialTrip.departure_pnr || '',
        departure_url: initialTrip.departure_url || '',
        departure_confirmation: initialTrip.departure_confirmation || '',
        departure_notes: initialTrip.departure_notes || '',
        include_departure_in_itinerary: initialTrip.include_departure_in_itinerary !== false,
        // Trip Status & Agreements
        trip_status: initialTrip.trip_status || 'draft',
        approved_at: initialTrip.approved_at || '',
        approval_reset_reason: initialTrip.approval_reset_reason || '',
        contract_link: initialTrip.contract_link || '',
        contract_status: initialTrip.contract_status || 'not_sent',
        contract_signed_at: initialTrip.contract_signed_at || '',
        terms_link: initialTrip.terms_link || '',
        terms_acknowledged: initialTrip.terms_acknowledged || false,
        card_auth_link: initialTrip.card_auth_link || '',
        card_auth_status: initialTrip.card_auth_status || 'not_sent',
        // Pricing
        price_currency: initialTrip.price_currency || 'USD',
        price_line_items: initialTrip.price_line_items || [],
        price_subtotal: initialTrip.price_subtotal || 0,
        price_taxes_fees: initialTrip.price_taxes_fees || 0,
        price_total: initialTrip.price_total || 0,
        // Deposit tracking
        price_deposit_amount: initialTrip.price_deposit_amount || 0,
        price_deposit_due_date: initialTrip.price_deposit_due_date || '',
        price_deposit_received_date: initialTrip.price_deposit_received_date || '',
        price_deposit_status: initialTrip.price_deposit_status || 'not_paid',
        // Installment plan
        price_installments: initialTrip.price_installments || [],
        price_payment_status: initialTrip.price_payment_status || 'not_collected',
        price_notes: initialTrip.price_notes || '',
        // Legacy
        price_deposit_required: initialTrip.price_deposit_required || 0,
        // International trip flag
        is_international: initialTrip.is_international || false,
        // Advisor Notes
        advisor_notes: initialTrip.advisor_notes || '',
        client_preferences: initialTrip.client_preferences || '',
        vendor_contacts: initialTrip.vendor_contacts || '',
        // Client Portal Customization
        client_banner_name: initialTrip.client_banner_name || '',
        journey_overview: initialTrip.journey_overview || '',
        // Reveal Controls
        reveal_destination: initialTrip.reveal_destination || 'full',
        reveal_destination_mask: initialTrip.reveal_destination_mask || 'A surprise ✨',
        reveal_dates: initialTrip.reveal_dates || 'full',
        reveal_dates_mask: initialTrip.reveal_dates_mask || 'Coming soon',
        reveal_weather: initialTrip.reveal_weather || 'full',
        reveal_itinerary: initialTrip.reveal_itinerary || 'full',
        reveal_documents_hidden_ids: initialTrip.reveal_documents_hidden_ids || [],
      });
      setPublishId(initialTrip.publish_id || null);
      setClientPortalToken(initialTrip.client_portal_token || null);
      setClientPortalEnabled(initialTrip.client_portal_enabled !== false);
      // Load workflow state
      setWorkflowStage(initialTrip.workflow_stage || 'consultation_complete');
      setClientVisibleTabs(initialTrip.client_visible_tabs || ['itinerary', 'estimate', 'prep']);
      // Load trip phase state
      setTripPhase(initialTrip.trip_phase || 'agreements_pending');
      if (initialTrip.id) {
        fetchItinerary(initialTrip.id);
        fetchMessages(initialTrip.id);
        fetchChangeRequests(initialTrip.id);
        fetchConfirmations(initialTrip.id);
        fetchPlanningState(initialTrip.id);
        // Fetch travelers for UnifiedTripDetails
        setTripId(initialTrip.id);
      }
    }
  }, [initialTrip]);
  
  // Fetch trip travelers when tripId changes
  useEffect(() => {
    if (tripId) {
      fetchTripTravelers();
    }
  }, [tripId]);
  
  // Fetch planning state for day-scoped planning
  const fetchPlanningState = async (id) => {
    try {
      const res = await axios.get(`${API}/travels/trips/${id}/ai-planning-state`);
      setPlanningState(res.data);
    } catch (e) {
      console.error('Error fetching planning state:', e);
    }
  };

  const fetchItinerary = async (id) => {
    try {
      const res = await axios.get(`${API}/travels/itinerary/${id}`);
      const days = res.data || [];
      setItinerary(days);
      
      const activitiesMap = {};
      for (const day of days) {
        const actRes = await axios.get(`${API}/travels/activities/${day.id}`);
        activitiesMap[day.id] = { morning: [], afternoon: [], evening: [] };
        for (const act of actRes.data || []) {
          if (activitiesMap[day.id][act.section]) {
            activitiesMap[day.id][act.section].push(act);
          }
        }
      }
      setActivities(activitiesMap);
      
      if (days.length > 0) {
        setExpandedDays({ [days[0].id]: true });
      }
    } catch (e) {
      console.error('Error fetching itinerary:', e);
      toast.error('Failed to load itinerary');
    }
  };

  const fetchMessages = async (id) => {
    try {
      const [internalRes, clientRes] = await Promise.all([
        axios.get(`${API}/travels/messages/${id}?thread_type=internal`),
        axios.get(`${API}/travels/messages/${id}?thread_type=client_chat`)
      ]);
      setInternalMessages(internalRes.data || []);
      setClientChatMessages(clientRes.data || []);
    } catch (e) {
      console.error('Error fetching messages:', e);
    }
  };

  const fetchChangeRequests = async (id) => {
    try {
      const res = await axios.get(`${API}/travels/change-requests/${id}`);
      setChangeRequests(res.data || []);
    } catch (e) {
      console.error('Error fetching change requests:', e);
    }
  };

  const fetchConfirmations = async (id) => {
    try {
      const res = await axios.get(`${API}/travels/confirmations/${id}`);
      setConfirmations(res.data || []);
    } catch (e) {
      console.error('Error fetching confirmations:', e);
    }
  };

  const fetchTripTravelers = async () => {
    if (!tripId) return;
    try {
      const res = await axios.get(`${API}/travels/trips/${tripId}/travelers`);
      setTripTravelers(res.data.travelers || []);
    } catch (e) {
      console.error('Error fetching trip travelers:', e);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
    setSaved(false);
  };

  const toggleTravelStyle = (style) => {
    const current = form.travel_styles || [];
    const updated = current.includes(style)
      ? current.filter(s => s !== style)
      : [...current, style];
    handleChange('travel_styles', updated);
  };

  // HARDENED SAVE - with proper feedback
  const handleSave = async () => {
    // Validate required fields - using single source of truth
    const hasDestinations = (form.destination_segments && form.destination_segments.length > 0) || form.destinations;
    const hasStartDate = getTripStartDate(form);
    const hasEndDate = getTripEndDate(form);
    
    if (!hasDestinations || !hasStartDate || !hasEndDate) {
      toast.error('Please fill in required fields: Destinations, Start Date, End Date');
      return;
    }
    
    setSaving(true);
    try {
      const payload = { ...form };
      
      if (tripId) {
        // Update existing trip
        const response = await axios.patch(`${API}/travels/trips/${tripId}`, payload);
        if (response.status === 200) {
          setHasUnsavedChanges(false);
          setSaved(true);
          toast.success('Trip saved successfully');
          setTimeout(() => setSaved(false), 3000);
          onSave?.();
        }
      } else {
        // Create new trip
        const response = await axios.post(`${API}/travels/trips`, payload);
        if (response.data?.id) {
          setTripId(response.data.id);
          setPublishId(response.data.publish_id || null);
          setHasUnsavedChanges(false);
          setSaved(true);
          toast.success('Trip created successfully');
          setTimeout(() => setSaved(false), 3000);
          onSave?.();
        }
      }
    } catch (e) {
      const errorMsg = e.response?.data?.detail || e.message || 'Unknown error';
      console.error('Save error:', errorMsg);
      toast.error(`Failed to save: ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  // HARDENED PUBLISH - with proper feedback and approval reset confirmation
  const publishTrip = async (skipConfirmation = false) => {
    if (!tripId) {
      toast.error('Please save the trip first before publishing');
      return;
    }
    
    // If trip is already approved and this is a republish, show confirmation dialog
    if (!skipConfirmation && clientPortalToken && form.trip_status === 'approved') {
      setShowRepublishConfirm(true);
      return;
    }
    
    setPublishing(true);
    try {
      const response = await axios.post(`${API}/travels/trips/${tripId}/publish`);
      if (response.data?.client_portal_token) {
        setClientPortalToken(response.data.client_portal_token);
        setPublishId(response.data.publish_id);
        setClientPortalEnabled(true);
        copyClientLink(response.data.client_portal_token);
        toast.success('Trip published! Magic link copied to clipboard.');
      }
    } catch (e) {
      const errorMsg = e.response?.data?.detail || e.message || 'Unknown error';
      console.error('Publish error:', errorMsg);
      toast.error(`Failed to publish: ${errorMsg}`);
    } finally {
      setPublishing(false);
    }
  };
  
  // Handle confirmed republish (resets client approval)
  const handleConfirmedRepublish = async () => {
    setShowRepublishConfirm(false);
    
    // Reset the trip status to draft since we're republishing
    try {
      await axios.patch(`${API}/travels/trips/${tripId}`, {
        trip_status: 'draft',
        approved_at: null,
        approval_reset_reason: 'Advisor republished itinerary'
      });
      setForm(prev => ({
        ...prev,
        trip_status: 'draft',
        approved_at: '',
        approval_reset_reason: 'Advisor republished itinerary'
      }));
      toast.info('Client approval has been reset');
    } catch (e) {
      console.error('Failed to reset approval:', e);
    }
    
    // Now proceed with the publish
    await publishTrip(true);
  };

  const regenerateToken = async () => {
    if (!tripId) return;
    setRegeneratingToken(true);
    try {
      const response = await axios.post(`${API}/travels/trips/${tripId}/regenerate-token`);
      if (response.data?.client_portal_token) {
        setClientPortalToken(response.data.client_portal_token);
        copyClientLink(response.data.client_portal_token);
        toast.success('New magic link generated');
      }
    } catch (e) {
      toast.error('Failed to regenerate link');
    } finally {
      setRegeneratingToken(false);
    }
  };

  const togglePortalAccess = async () => {
    if (!tripId) return;
    try {
      const newEnabled = !clientPortalEnabled;
      await axios.post(`${API}/travels/trips/${tripId}/toggle-portal?enabled=${newEnabled}`);
      setClientPortalEnabled(newEnabled);
      toast.success(newEnabled ? 'Client portal enabled' : 'Client portal disabled');
    } catch (e) {
      toast.error('Failed to toggle portal access');
    }
  };

  const copyClientLink = (token = clientPortalToken) => {
    if (!token) return;
    const link = `${window.location.origin}/trip/${token}`;
    navigator.clipboard.writeText(link);
    setLinkCopied(true);
    toast.success('Magic link copied to clipboard');
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const openClientPortal = () => {
    if (!clientPortalToken) {
      toast.error('Please publish the trip first');
      return;
    }
    window.open(`${window.location.origin}/trip/${clientPortalToken}`, '_blank');
  };

  // AUTO-SAVE: Debounced save on form changes
  useEffect(() => {
    // Skip auto-save during initial load
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      return;
    }
    
    // Skip if no trip ID (new trip not yet saved)
    if (!tripId) return;
    
    // Skip if required fields are missing - using single source of truth
    const hasDestinations = (form.destination_segments && form.destination_segments.length > 0) || form.destinations;
    const hasStartDate = getTripStartDate(form);
    const hasEndDate = getTripEndDate(form);
    if (!hasDestinations || !hasStartDate || !hasEndDate) return;
    
    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // Set new timer for debounced auto-save (1.5 second delay)
    autoSaveTimerRef.current = setTimeout(async () => {
      setAutoSaving(true);
      try {
        await axios.patch(`${API}/travels/trips/${tripId}`, form);
        setHasUnsavedChanges(false);
        // Subtle feedback - no toast for auto-save
      } catch (e) {
        console.error('Auto-save error:', e);
        // Silent fail for auto-save, user can manually save
      } finally {
        setAutoSaving(false);
      }
    }, 1500);
    
    // Cleanup
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [form, tripId]);

  // NOTIFY CLIENT: Send notification when changes are complete
  const notifyClient = async () => {
    if (!tripId) {
      toast.error('Please save the trip first');
      return;
    }
    
    if (!form.client_id) {
      toast.error('No client linked to this trip');
      return;
    }
    
    setNotifyingClient(true);
    try {
      // Send a notification message to the client
      await axios.post(`${API}/travels/trips/${tripId}/notify-client`, {
        message: 'Your advisor has completed the requested changes to your itinerary. Please review the updated itinerary at your convenience.'
      });
      toast.success('Client notified successfully');
    } catch (e) {
      const errorMsg = e.response?.data?.detail || e.message || 'Failed to notify client';
      toast.error(errorMsg);
    } finally {
      setNotifyingClient(false);
    }
  };

  // Recalculate price totals with per-person pricing
  const recalculatePrices = (lineItems) => {
    const numTravelers = form.num_travelers || 1;
    const subtotal = lineItems.reduce((sum, item) => {
      // If per_person_cost is set, multiply by travelers
      if (item.per_person_cost && item.quantity) {
        return sum + (item.per_person_cost * item.quantity * numTravelers);
      }
      return sum + (item.subtotal || 0);
    }, 0);
    setForm(prev => ({
      ...prev,
      price_subtotal: subtotal,
      price_total: subtotal + (prev.price_taxes_fees || 0)
    }));
  };

  // Calculate remaining balance
  const calculateRemainingBalance = () => {
    const total = form.price_total || 0;
    const depositPaid = form.price_deposit_status === 'paid' ? (form.price_deposit_amount || 0) : 0;
    const installmentsPaid = (form.price_installments || [])
      .filter(inst => inst.status === 'paid')
      .reduce((sum, inst) => sum + (inst.amount || 0), 0);
    return total - depositPaid - installmentsPaid;
  };

  // Generate installment plan
  const generateInstallmentPlan = (numPayments, startDate) => {
    if (!numPayments || numPayments < 1) return;
    const remainingAfterDeposit = (form.price_total || 0) - (form.price_deposit_amount || 0);
    const amountPerPayment = Math.round((remainingAfterDeposit / numPayments) * 100) / 100;
    
    const installments = [];
    const start = startDate ? new Date(startDate) : new Date();
    
    for (let i = 0; i < numPayments; i++) {
      const dueDate = new Date(start);
      dueDate.setDate(dueDate.getDate() + (i + 1) * 30); // Monthly payments
      installments.push({
        id: `inst-${Date.now()}-${i}`,
        amount: i === numPayments - 1 
          ? Math.round((remainingAfterDeposit - amountPerPayment * (numPayments - 1)) * 100) / 100 // Last payment gets remainder
          : amountPerPayment,
        due_date: dueDate.toISOString().split('T')[0],
        status: 'due',
        date_paid: ''
      });
    }
    handleChange('price_installments', installments);
  };

  // HARDENED GENERATE - with proper feedback and DB write
  const handleGenerateClick = () => {
    if (!tripId) {
      toast.error('Please save the trip first before generating itinerary');
      return;
    }
    if (itinerary.length > 0) {
      setShowGenerateDialog(true);
    } else {
      generateItinerary('replace');
    }
  };

  const generateItinerary = async (mode) => {
    if (!tripId) {
      toast.error('Trip must be saved first');
      return;
    }
    
    setGenerating(true);
    setShowGenerateDialog(false);
    toast.info('Generating itinerary... This may take a moment.');
    
    try {
      // Delete existing if replacing
      if (mode === 'replace') {
        for (const day of itinerary) {
          await axios.delete(`${API}/travels/itinerary/${day.id}`).catch(() => {});
        }
      }
      
      const response = await axios.post(`${API}/travels/trips/${tripId}/generate-itinerary`, { mode });
      
      if (response.data?.success) {
        toast.success(response.data.message || 'Itinerary generated successfully');
        await fetchItinerary(tripId);
      } else {
        toast.error(response.data?.message || 'Failed to generate itinerary');
        console.error('Generate response:', response.data);
      }
    } catch (e) {
      const errorMsg = e.response?.data?.detail || e.message || 'Unknown error';
      console.error('Generate error:', errorMsg);
      toast.error(`Failed to generate: ${errorMsg}`);
    } finally {
      setGenerating(false);
    }
  };

  // Day management with error handling
  const addDay = async () => {
    if (!tripId) {
      toast.error('Save the trip first');
      return;
    }
    const dayNum = itinerary.length + 1;
    try {
      const res = await axios.post(`${API}/travels/itinerary`, { 
        trip_id: tripId, 
        day_number: dayNum,
        visible_to_client: true
      });
      setItinerary([...itinerary, res.data]);
      setActivities(prev => ({ ...prev, [res.data.id]: { morning: [], afternoon: [], evening: [] } }));
      setExpandedDays(prev => ({ ...prev, [res.data.id]: true }));
      toast.success(`Day ${dayNum} added`);
    } catch (e) {
      toast.error('Failed to add day');
      console.error(e);
    }
  };

  const updateDay = async (dayId, field, value) => {
    setItinerary(itinerary.map(d => d.id === dayId ? { ...d, [field]: value } : d));
    try {
      await axios.patch(`${API}/travels/itinerary/${dayId}`, { [field]: value });
    } catch (e) {
      toast.error('Failed to update day');
      console.error(e);
    }
  };

  const deleteDay = async (dayId) => {
    try {
      await axios.delete(`${API}/travels/itinerary/${dayId}`);
      setItinerary(itinerary.filter(d => d.id !== dayId));
      setActivities(prev => {
        const copy = { ...prev };
        delete copy[dayId];
        return copy;
      });
      toast.success('Day deleted');
    } catch (e) {
      toast.error('Failed to delete day');
      console.error(e);
    }
  };

  // Activity management with error handling
  const addActivity = async (dayId, section) => {
    const current = activities[dayId]?.[section] || [];
    if (current.length >= 5) {
      toast.error('Maximum 5 activities per section');
      return;
    }
    
    try {
      const res = await axios.post(`${API}/travels/activities`, {
        day_id: dayId,
        section,
        order: current.length,
        name: '',
        location_name: '',
        time: '',
        address: '',
        emoji: '',
        visible_to_client: true
      });
      setActivities(prev => ({
        ...prev,
        [dayId]: {
          ...prev[dayId],
          [section]: [...(prev[dayId]?.[section] || []), res.data]
        }
      }));
    } catch (e) {
      toast.error('Failed to add activity');
      console.error(e);
    }
  };

  const updateActivity = async (activityId, dayId, section, field, value) => {
    setActivities(prev => ({
      ...prev,
      [dayId]: {
        ...prev[dayId],
        [section]: prev[dayId][section].map(a => 
          a.id === activityId ? { ...a, [field]: value } : a
        )
      }
    }));
    try {
      await axios.patch(`${API}/travels/activities/${activityId}`, { [field]: value });
    } catch (e) {
      console.error('Failed to update activity:', e);
    }
  };

  const deleteActivity = async (activityId, dayId, section) => {
    try {
      await axios.delete(`${API}/travels/activities/${activityId}`);
      setActivities(prev => ({
        ...prev,
        [dayId]: {
          ...prev[dayId],
          [section]: prev[dayId][section].filter(a => a.id !== activityId)
        }
      }));
    } catch (e) {
      toast.error('Failed to delete activity');
      console.error(e);
    }
  };

  // Internal Notes with error handling
  const sendInternalMessage = async () => {
    if (!newInternalMessage.trim() || !tripId) return;
    try {
      const res = await axios.post(`${API}/travels/messages`, {
        trip_id: tripId,
        sender: 'advisor',
        message: newInternalMessage.trim(),
        thread_type: 'internal',
        visible_to_client: false
      });
      setInternalMessages([...internalMessages, res.data]);
      setNewInternalMessage('');
    } catch (e) {
      toast.error('Failed to save note');
      console.error(e);
    }
  };

  // Client Chat with error handling + Communications Hub sync
  const sendClientMessage = async () => {
    if (!newClientMessage.trim() || !tripId) return;
    try {
      const res = await axios.post(`${API}/travels/messages`, {
        trip_id: tripId,
        sender: 'advisor',
        message: newClientMessage.trim(),
        thread_type: 'client_chat',
        visible_to_client: true
      });
      setClientChatMessages([...clientChatMessages, res.data]);
      const msgText = newClientMessage.trim();
      setNewClientMessage('');
      toast.success('Message sent to client');
      // Sync to Communications Hub (fire-and-forget)
      axios.post(`${API}/communications`, {
        comm_type: 'client_email',
        subject: `Message to client: ${msgText.substring(0, 50)}${msgText.length > 50 ? '...' : ''}`,
        summary: msgText,
        direction: 'outbound',
        counterparty_type: 'client',
        counterparty_name: form.client_name || '',
        trip_id: tripId,
        priority: 'normal',
      }).catch(() => {}); // Non-blocking sync
    } catch (e) {
      toast.error('Failed to send message');
      console.error(e);
    }
  };

  // Edit message
  const handleEditMessage = async (msgId, newText) => {
    if (!newText.trim()) return;
    try {
      const res = await axios.patch(`${API}/travels/messages/${msgId}`, { message: newText.trim() });
      setClientChatMessages(prev => prev.map(m => m.id === msgId ? { ...m, message: newText.trim(), edited_at: res.data?.edited_at } : m));
      setInternalMessages(prev => prev.map(m => m.id === msgId ? { ...m, message: newText.trim(), edited_at: res.data?.edited_at } : m));
      setEditingMsgId(null);
      setEditingMsgText('');
      toast.success('Message updated');
    } catch (e) {
      toast.error('Failed to edit message');
    }
  };

  // Delete message
  const handleDeleteMessage = async (msgId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await axios.delete(`${API}/travels/messages/${msgId}`);
      setClientChatMessages(prev => prev.filter(m => m.id !== msgId));
      setInternalMessages(prev => prev.filter(m => m.id !== msgId));
      toast.success('Message deleted');
    } catch (e) {
      toast.error('Failed to delete message');
    }
  };

  // Change Requests with error handling
  const updateChangeRequest = async (requestId, updates) => {
    try {
      await axios.patch(`${API}/travels/change-requests/${requestId}`, updates);
      setChangeRequests(prev => prev.map(r => 
        r.id === requestId ? { ...r, ...updates } : r
      ));
      toast.success('Request updated');
    } catch (e) {
      toast.error('Failed to update request');
      console.error(e);
    }
  };

  // Notify client after update
  const notifyClientAfterUpdate = async (requestId) => {
    if (!tripId || !form.client_chat_enabled) {
      toast.error('Enable client chat to notify client');
      return;
    }
    
    const request = changeRequests.find(r => r.id === requestId);
    const message = `Your requested change has been addressed: "${request?.message?.substring(0, 50)}..." - The itinerary has been updated.`;
    
    try {
      const res = await axios.post(`${API}/travels/messages`, {
        trip_id: tripId,
        sender: 'advisor',
        message,
        thread_type: 'client_chat',
        visible_to_client: true
      });
      setClientChatMessages([...clientChatMessages, res.data]);
      toast.success('Client notified');
    } catch (e) {
      toast.error('Failed to notify client');
    }
  };

  // Advance trip status with gating rules
  const handleAdvanceStatus = async (targetStatus, adminOverride = false) => {
    if (!tripId) {
      toast.error('Save trip first');
      return;
    }
    
    try {
      const params = new URLSearchParams({ target_status: targetStatus });
      if (adminOverride) params.append('admin_override', 'true');
      
      const res = await axios.post(`${API}/travels/trips/${tripId}/advance-status?${params.toString()}`);
      setForm(prev => ({ 
        ...prev, 
        trip_status: targetStatus,
        ...(res.data.approved_at ? { approved_at: res.data.approved_at } : {})
      }));
      toast.success(`Status updated to ${targetStatus.replace(/_/g, ' ')}`);
      onSave?.();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to advance status';
      toast.error(msg);
    }
  };

  // AI Assistant with proper error handling
  const sendAiMessage = async () => {
    if (!aiInput.trim()) return;
    const userMsg = aiInput.trim();
    setAiChat(prev => [...prev, { role: 'user', content: userMsg }]);
    setAiInput('');
    setAiLoading(true);
    
    try {
      const res = await axios.post(`${API}/travels/ai-assistant`, {
        trip_id: tripId,
        message: userMsg,
        context: {
          destinations: getTripDestinationsDisplay(form) || form.destinations,
          dates: getTripDateRangeFormatted(form),
          travelers: form.num_travelers,
          styles: form.travel_styles,
          constraints: form.constraints
        }
      });
      
      if (res.data.error) {
        setAiChat(prev => [...prev, { role: 'assistant', content: res.data.message, error: true }]);
        toast.error(`AI Error: ${res.data.error}`);
      } else {
        setAiChat(prev => [...prev, { role: 'assistant', content: res.data.message }]);
        if (res.data.suggestions?.length > 0) {
          setSuggestions(res.data.suggestions);
        }
      }
    } catch (e) {
      const errorMsg = e.response?.data?.detail || e.message || 'Connection failed';
      console.error('AI Error:', errorMsg);
      setAiChat(prev => [...prev, { role: 'assistant', content: `Unable to connect: ${errorMsg}`, error: true }]);
      setAiInput(userMsg); // Keep message for retry
      toast.error(`AI unavailable: ${errorMsg}`);
    } finally {
      setAiLoading(false);
    }
  };

  const insertSuggestion = async (suggestion) => {
    if (!suggestion.day || !suggestion.section) {
      toast.error('Suggestion missing day or section');
      return;
    }
    
    const targetDay = itinerary.find(d => d.day_number === suggestion.day);
    if (!targetDay) {
      toast.error(`Day ${suggestion.day} not found. Add the day first.`);
      return;
    }
    
    try {
      const res = await axios.post(`${API}/travels/activities`, {
        day_id: targetDay.id,
        section: suggestion.section.toLowerCase(),
        order: (activities[targetDay.id]?.[suggestion.section.toLowerCase()] || []).length,
        name: suggestion.title,
        description: suggestion.why,
        website_url: suggestion.link || '',
        photo_url: suggestion.photo || '',
        visible_to_client: true
      });
      
      setActivities(prev => ({
        ...prev,
        [targetDay.id]: {
          ...prev[targetDay.id],
          [suggestion.section.toLowerCase()]: [
            ...(prev[targetDay.id]?.[suggestion.section.toLowerCase()] || []),
            res.data
          ]
        }
      }));
      
      setSuggestions(prev => prev.filter(s => s !== suggestion));
      toast.success(`Added "${suggestion.title}" to Day ${suggestion.day}`);
    } catch (e) {
      toast.error('Failed to insert suggestion');
      console.error(e);
    }
  };

  const toggleDayExpanded = (dayId) => {
    setExpandedDays(prev => ({ ...prev, [dayId]: !prev[dayId] }));
  };

  const linkedClient = clients?.find(c => c.id === form.client_id);

  // Calculate total trip days using effective dates
  const { startDate: effectiveStartDate, endDate: effectiveEndDate } = getEffectiveDates(form);
  const totalTripDays = calculateTripDays(effectiveStartDate, effectiveEndDate);

  return (
    <TripPlanningProvider 
      tripId={tripId} 
      startDate={effectiveStartDate} 
      endDate={effectiveEndDate}
    >
    <div data-testid="trip-detail" className="min-h-screen bg-[#F5F1EB]">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="bg-white border-b border-[rgba(0,0,0,0.06)] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          {/* Uniform Back Button */}
          <button 
            onClick={onBack} 
            className="flex items-center gap-2 text-[#8C8C8C] hover:text-[#1A1A1A] transition-colors text-sm mb-3"
            data-testid="vt-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Return to Dashboard
          </button>
          
          {/* MOBILE-FIRST HEADER */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            {/* Trip Info */}
            <div className="flex items-center gap-3 min-w-0">
              {/* VT Home Logo */}
              <button
                onClick={onBack}
                className="w-9 h-9 md:w-10 md:h-10 shrink-0 rounded-lg bg-gradient-to-br from-[#C8A85A] to-[#A8883A] flex items-center justify-center shadow-md hover:shadow-lg transition-all hover:scale-105"
                data-testid="vt-home-btn"
                title="Return to Vérault Travels Dashboard"
              >
                <Plane className="w-4 h-4 md:w-5 md:h-5 text-white" />
              </button>
              
              <div className="min-w-0 flex-1">
                <h1 className="font-serif text-base md:text-xl text-[#1A1A1A] truncate">
                  {form.internal_trip_name || form.trip_title || form.destinations || 'New Trip'}
                </h1>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5">
                  {form.system_trip_id && (
                    <span className="text-[10px] md:text-xs font-mono bg-[#F5F1EB] px-1.5 py-0.5 rounded text-[#8C8A86]">
                      {form.system_trip_id}
                    </span>
                  )}
                  {form.is_international && (
                    <span 
                      className="text-[10px] md:text-xs px-1.5 py-0.5 rounded-full bg-[#C8A85A]/15 text-[#C8A85A] font-medium flex items-center gap-0.5"
                      data-testid="international-trip-badge"
                    >
                      <Plane className="w-2.5 h-2.5 md:w-3 md:h-3" />
                      Intl
                    </span>
                  )}
                  <span className="text-xs md:text-sm text-[#8C8C8C]">
                    {getTripDateRangeFormatted(form) || 'Dates not set'}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Action Buttons - Mobile optimized */}
            <div className="flex items-center gap-2 ml-12 md:ml-0">
              {hasUnsavedChanges && <span className="text-[10px] md:text-xs text-[#C8A85A] font-medium">● Unsaved</span>}
              {saved && <span className="text-[10px] md:text-xs text-[#8DA399] font-medium hidden md:inline">✓ Saved</span>}
              
              {/* View Portal - Icon only on mobile */}
              {publishId && (
                <Button variant="outline" onClick={openClientPortal} className="border-[#8DA399] text-[#8DA399] px-2 md:px-3" size="sm">
                  <ExternalLink className="w-4 h-4 md:mr-2" />
                  <span className="hidden md:inline">View Member Suite</span>
                </Button>
              )}
              
              {/* Copy Link - Icon only on mobile */}
              {clientPortalToken && (
                <Button variant="outline" onClick={() => copyClientLink()} className="border-[#8C8C8C] text-[#8C8C8C] px-2 md:px-3" size="sm">
                  {linkCopied ? <Check className="w-4 h-4 md:mr-2" /> : <Copy className="w-4 h-4 md:mr-2" />}
                  <span className="hidden md:inline">{linkCopied ? 'Copied!' : 'Copy Link'}</span>
                </Button>
              )}
              
              <Button 
                variant="outline"
                onClick={() => publishTrip()} 
                disabled={!tripId || publishing} 
                className="border-[#C8A85A] text-[#C8A85A] px-2 md:px-3"
                size="sm"
                data-testid="publish-trip-btn"
              >
                {publishing ? <Loader2 className="w-4 h-4 md:mr-2 animate-spin" /> : <Share2 className="w-4 h-4 md:mr-2" />}
                <span className="hidden md:inline">{clientPortalToken ? 'Republish' : 'Publish'}</span>
              </Button>
              
              <Button onClick={handleSave} disabled={saving || autoSaving} className="bg-[#2C2C2C] text-white px-2 md:px-3" size="sm">
                {saving ? <Loader2 className="w-4 h-4 md:mr-2 animate-spin" /> : autoSaving ? <Loader2 className="w-4 h-4 md:mr-2 animate-spin" /> : <Save className="w-4 h-4 md:mr-2" />}
                <span className="hidden md:inline">{saving ? 'Saving...' : autoSaving ? 'Auto-saving...' : saved ? 'Saved!' : 'Save'}</span>
              </Button>
            </div>
          </div>
          
          {/* Magic Link Controls - Compact on mobile */}
          {clientPortalToken && (
            <div className="mt-2 p-2 md:p-3 bg-[#8DA399]/10 rounded-lg">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <span className="text-xs text-[#8DA399] truncate font-mono">
                  {window.location.origin}/trip/{clientPortalToken.substring(0, 8)}...
                </span>
                <div className="flex items-center gap-1.5 md:gap-2">
                  <Button variant="ghost" size="sm" onClick={() => copyClientLink()} className="text-xs h-7 px-2">
                    {linkCopied ? 'Copied!' : 'Copy'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={regenerateToken} disabled={regeneratingToken} className="text-xs h-7 px-2">
                    {regeneratingToken ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Regen'}
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={togglePortalAccess}
                    className={`text-xs h-7 px-2 ${clientPortalEnabled ? 'text-[#8DA399]' : 'text-[#B37E6B]'}`}
                  >
                    {clientPortalEnabled ? 'On' : 'Off'}
                  </Button>
                </div>
              </div>
              <p className="text-[10px] md:text-xs text-[#8C8C8C] mt-1 hidden md:block">
                {clientPortalEnabled 
                  ? 'Clients can access via magic link (no login required)'
                  : 'Portal disabled. Enable for client access.'}
              </p>
            </div>
          )}
          
          {/* Section Tabs - LOCKED ORDER per spec - Mobile optimized */}
          <div className="flex gap-0.5 md:gap-1 mt-4 -mb-px overflow-x-auto scrollbar-hide pb-[1px]">
            {[
              { key: 'command_center', label: 'Command Center', shortLabel: 'Command' },
              { key: 'trip_details', label: 'Trip Details', shortLabel: 'Details' },
              { key: 'ai-assistant', label: 'AI Assistant', shortLabel: 'AI' },
              { key: 'itinerary', label: 'Itinerary', shortLabel: 'Itinerary' },
              { key: 'logistics', label: 'Logistics', shortLabel: 'Logistics' },
              { key: 'client_lounge', label: 'Client Lounge', shortLabel: 'Client' },
              { key: 'messages', label: 'Messages', shortLabel: 'Messages' },
              { key: 'agreements', label: 'Agreements', shortLabel: 'Agree' },
              { key: 'advisor_notes', label: 'Advisor Notes', shortLabel: 'Notes' },
            ].map(tab => (
              <button
                key={tab.key}
                data-testid={`tab-${tab.key}`}
                onClick={() => setActiveSection(tab.key)}
                className={`px-2 md:px-4 py-2 text-xs md:text-sm rounded-t-lg transition-colors whitespace-nowrap flex-shrink-0 ${
                  activeSection === tab.key 
                    ? 'bg-[#F5F1EB] text-[#1A1A1A] border-t border-x border-[rgba(0,0,0,0.06)]' 
                    : 'text-[#8C8C8C] hover:text-[#1A1A1A]'
                }`}
              >
                <span className="hidden md:inline">{tab.label}</span>
                <span className="md:hidden">{tab.shortLabel}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* COMMAND CENTER TAB - Navigational hub + controller */}
        {activeSection === 'command_center' && (
          <div className="space-y-6">
            <CommandCenterPanel 
              tripId={tripId}
              form={form}
              itinerary={itinerary}
              onNavigate={(tab, section) => {
                if (tab === 'calendar') {
                  window.location.href = '/travels?view=calendar';
                } else {
                  setActiveSection(tab);
                }
              }}
              onFormUpdate={(updates) => {
                setForm(prev => ({ ...prev, ...updates }));
              }}
            />
          </div>
        )}

        {/* Trip Details Section - UNIFIED (REARCHITECTED) */}
        {activeSection === 'trip_details' && (
          <UnifiedTripDetails 
            tripId={tripId}
            form={form}
            onFormChange={(updates) => setForm(prev => ({ ...prev, ...updates }))}
            travelers={tripTravelers}
            onTravelersChange={fetchTripTravelers}
            linkedClient={linkedClient}
            clients={clients}
            onClientChange={(client) => {
              setForm(prev => ({ ...prev, client_id: client.id, client_name: client.full_name }));
            }}
            onNavigate={(tab, section) => {
              setActiveSection(tab);
              if (section) {
                setTimeout(() => {
                  const el = document.querySelector(`[data-section="${section}"]`);
                  el?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }
            }}
          />
        )}


        {/* AI Assistant Section - Enhanced with Memory & Deep Integration */}
        {activeSection === 'ai-assistant' && (
          <TripAIAssistant 
            tripId={tripId}
            form={form}
            startDate={getTripStartDate(form)}
            endDate={getTripEndDate(form)}
            totalDays={totalTripDays}
            onNavigate={(tab, section) => {
              setActiveSection(tab);
              // Scroll to section if specified
              if (section) {
                setTimeout(() => {
                  const el = document.querySelector(`[data-section="${section}"]`);
                  el?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }
            }}
            onGoToItinerary={(dayNumber) => {
              setActiveSection('itinerary');
              setTimeout(() => {
                const dayEl = document.querySelector(`[data-day-number="${dayNumber}"]`);
                dayEl?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 100);
            }}
          />
        )}

        {/* ITINERARY TAB - Itinerary Engine v3 */}
        {activeSection === 'itinerary' && (
          <div className="space-y-4" data-testid="itinerary-tab">
            {/* Itinerary Header with Trip Days Summary */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <h2 className="font-serif text-xl text-[#1A1A1A]">Trip Itinerary</h2>
                  {totalTripDays > 0 && (
                    <span className="text-sm px-3 py-1 rounded-full bg-[#C8A85A]/10 text-[#C8A85A] font-medium">
                      {totalTripDays} days
                    </span>
                  )}
                </div>
                <p className="text-sm text-[#8C8C8C] mt-1">
                  {getDestinationDisplayLocal(form)} • {getTripStartDate(form) && getTripEndDate(form) ? getTripDateRangeFormatted(form) : 'Set dates in Trip Details'}
                </p>
              </div>
              <div className="flex items-center gap-2 md:gap-3">
                {/* Sync to AI Assistant */}
                <Button
                  variant="outline"
                  onClick={() => setActiveSection('ai-assistant')}
                  className="border-[#C8A85A] text-[#C8A85A] text-xs md:text-sm px-2 md:px-3"
                  size="sm"
                >
                  <Sparkles className="w-4 h-4 md:mr-2" />
                  <span className="hidden md:inline">AI Assistant</span>
                </Button>
                {/* Publish to Client Portal */}
                <Button
                  onClick={() => publishTrip()}
                  className="bg-[#C8A85A] hover:bg-[#B39049] text-xs md:text-sm px-2 md:px-3"
                  size="sm"
                  disabled={publishing}
                >
                  {publishing ? (
                    <Loader2 className="w-4 h-4 md:mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 md:mr-2" />
                  )}
                  <span className="hidden md:inline">Publish</span>
                </Button>
              </div>
            </div>
            
            <ItineraryEngine
              tripId={tripId}
              startDate={getTripStartDate(form)}
              endDate={getTripEndDate(form)}
              onNavigateToAI={() => setActiveSection('ai-assistant')}
            />
          </div>
        )}

        {/* Prices Tab */}
        {activeSection === 'prices' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-serif text-xl text-[#1A1A1A]">Trip Pricing</h2>
                <p className="text-sm text-[#8C8C8C]">
                  {form.num_travelers} traveler{form.num_travelers !== 1 ? 's' : ''} • Remaining Balance: {form.price_currency} {calculateRemainingBalance().toFixed(2)}
                </p>
              </div>
              <Select value={form.price_currency} onValueChange={(v) => handleChange('price_currency', v)}>
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                  <SelectItem value="CAD">CAD</SelectItem>
                  <SelectItem value="AUD">AUD</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Line Items with Per-Person Pricing */}
            <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#F9F8F4]">
                  <tr>
                    <th className="text-left text-xs text-[#8C8C8C] uppercase p-3">Category</th>
                    <th className="text-left text-xs text-[#8C8C8C] uppercase p-3">Description</th>
                    <th className="text-right text-xs text-[#8C8C8C] uppercase p-3 w-20">Qty</th>
                    <th className="text-right text-xs text-[#8C8C8C] uppercase p-3 w-28">Per Person</th>
                    <th className="text-right text-xs text-[#8C8C8C] uppercase p-3 w-28">Subtotal</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.price_line_items.map((item, idx) => (
                    <tr key={idx} className="border-t border-[rgba(0,0,0,0.06)]">
                      <td className="p-2">
                        <Select
                          value={item.category || ''}
                          onValueChange={(v) => {
                            const updated = [...form.price_line_items];
                            updated[idx] = { ...updated[idx], category: v };
                            handleChange('price_line_items', updated);
                            recalculatePrices(updated);
                          }}
                        >
                          <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="flights">Flights</SelectItem>
                            <SelectItem value="lodging">Lodging</SelectItem>
                            <SelectItem value="activities">Activities</SelectItem>
                            <SelectItem value="transport">Transport</SelectItem>
                            <SelectItem value="dining">Dining</SelectItem>
                            <SelectItem value="fees">Fees</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.description || ''}
                          onChange={(e) => {
                            const updated = [...form.price_line_items];
                            updated[idx] = { ...updated[idx], description: e.target.value };
                            handleChange('price_line_items', updated);
                          }}
                          className="h-8 text-sm"
                          placeholder="Description"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity || 1}
                          onChange={(e) => {
                            const updated = [...form.price_line_items];
                            const qty = parseInt(e.target.value) || 1;
                            const perPerson = updated[idx].per_person_cost || 0;
                            updated[idx] = { 
                              ...updated[idx], 
                              quantity: qty, 
                              subtotal: qty * perPerson * (form.num_travelers || 1)
                            };
                            handleChange('price_line_items', updated);
                            recalculatePrices(updated);
                          }}
                          className="h-8 text-sm text-right"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.per_person_cost || ''}
                          onChange={(e) => {
                            const updated = [...form.price_line_items];
                            const cost = parseFloat(e.target.value) || 0;
                            const qty = updated[idx].quantity || 1;
                            updated[idx] = { 
                              ...updated[idx], 
                              per_person_cost: cost, 
                              subtotal: qty * cost * (form.num_travelers || 1)
                            };
                            handleChange('price_line_items', updated);
                            recalculatePrices(updated);
                          }}
                          className="h-8 text-sm text-right"
                          placeholder="0.00"
                        />
                      </td>
                      <td className="p-2 text-right text-sm font-medium">
                        {form.price_currency} {(item.subtotal || 0).toFixed(2)}
                      </td>
                      <td className="p-2">
                        <button
                          onClick={() => {
                            const updated = form.price_line_items.filter((_, i) => i !== idx);
                            handleChange('price_line_items', updated);
                            recalculatePrices(updated);
                          }}
                          className="p-1 text-[#B37E6B] hover:bg-[#B37E6B]/10 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-3 border-t border-[rgba(0,0,0,0.06)]">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    handleChange('price_line_items', [...form.price_line_items, { category: '', description: '', quantity: 1, per_person_cost: 0, subtotal: 0 }]);
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Line Item
                </Button>
              </div>
            </div>

            {/* Totals */}
            <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-[#8C8C8C]">Subtotal ({form.num_travelers} travelers)</span>
                  <span className="font-medium">{form.price_currency} {(form.price_subtotal || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#8C8C8C]">Est. Taxes & Fees</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.price_taxes_fees || ''}
                    onChange={(e) => {
                      const taxes = parseFloat(e.target.value) || 0;
                      handleChange('price_taxes_fees', taxes);
                      handleChange('price_total', (form.price_subtotal || 0) + taxes);
                    }}
                    className="w-32 h-8 text-right"
                    placeholder="0.00"
                  />
                </div>
                <div className="flex justify-between pt-3 border-t border-[rgba(0,0,0,0.06)]">
                  <span className="font-medium text-lg">Total Estimate</span>
                  <span className="font-serif text-xl text-[#C8A85A]">{form.price_currency} {(form.price_total || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Deposit Tracking */}
            <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6">
              <h3 className="font-serif text-lg text-[#1A1A1A] mb-4">Deposit</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="text-xs text-[#8C8C8C] uppercase mb-1 block">Deposit Amount</label>
                  <div className="flex items-center gap-2">
                    <span className="text-[#8C8C8C]">{form.price_currency}</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.price_deposit_amount || ''}
                      onChange={(e) => handleChange('price_deposit_amount', parseFloat(e.target.value) || 0)}
                      className="text-right"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#8C8C8C] uppercase mb-1 block">Due Date</label>
                  <Input
                    type="date"
                    value={form.price_deposit_due_date || ''}
                    onChange={(e) => handleChange('price_deposit_due_date', e.target.value)}
                    className="bg-[#EEE9E2]"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#8C8C8C] uppercase mb-1 block">Status</label>
                  <Select value={form.price_deposit_status} onValueChange={(v) => handleChange('price_deposit_status', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_paid">Not Paid</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-[#8C8C8C] uppercase mb-1 block">Date Received</label>
                  <Input
                    type="date"
                    value={form.price_deposit_received_date || ''}
                    onChange={(e) => handleChange('price_deposit_received_date', e.target.value)}
                    className="bg-[#EEE9E2]"
                    disabled={form.price_deposit_status !== 'paid'}
                  />
                </div>
              </div>
            </div>

            {/* Installment Plan */}
            <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-lg text-[#1A1A1A]">Payment Schedule</h3>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="1"
                    max="12"
                    placeholder="# Payments"
                    className="w-28 h-8"
                    id="num-installments"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const numPayments = parseInt(document.getElementById('num-installments').value) || 3;
                      generateInstallmentPlan(numPayments, form.price_deposit_due_date);
                    }}
                  >
                    Generate Plan
                  </Button>
                </div>
              </div>
              
              {(form.price_installments?.length > 0) ? (
                <table className="w-full">
                  <thead className="bg-[#F9F8F4]">
                    <tr>
                      <th className="text-left text-xs text-[#8C8C8C] uppercase p-3">#</th>
                      <th className="text-right text-xs text-[#8C8C8C] uppercase p-3">Amount</th>
                      <th className="text-left text-xs text-[#8C8C8C] uppercase p-3">Due Date</th>
                      <th className="text-left text-xs text-[#8C8C8C] uppercase p-3">Status</th>
                      <th className="text-left text-xs text-[#8C8C8C] uppercase p-3">Date Paid</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.price_installments.map((inst, idx) => (
                      <tr key={inst.id || idx} className="border-t border-[rgba(0,0,0,0.06)]">
                        <td className="p-2 text-sm text-[#8C8C8C]">{idx + 1}</td>
                        <td className="p-2">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={inst.amount || ''}
                            onChange={(e) => {
                              const updated = [...form.price_installments];
                              updated[idx] = { ...updated[idx], amount: parseFloat(e.target.value) || 0 };
                              handleChange('price_installments', updated);
                            }}
                            className="h-8 text-sm text-right w-28"
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            type="date"
                            value={inst.due_date || ''}
                            onChange={(e) => {
                              const updated = [...form.price_installments];
                              updated[idx] = { ...updated[idx], due_date: e.target.value };
                              handleChange('price_installments', updated);
                            }}
                            className="h-8 text-sm"
                          />
                        </td>
                        <td className="p-2">
                          <Select
                            value={inst.status || 'due'}
                            onValueChange={(v) => {
                              const updated = [...form.price_installments];
                              updated[idx] = { ...updated[idx], status: v };
                              handleChange('price_installments', updated);
                            }}
                          >
                            <SelectTrigger className="h-8 text-sm w-24">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="due">Due</SelectItem>
                              <SelectItem value="paid">Paid</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-2">
                          <Input
                            type="date"
                            value={inst.date_paid || ''}
                            onChange={(e) => {
                              const updated = [...form.price_installments];
                              updated[idx] = { ...updated[idx], date_paid: e.target.value };
                              handleChange('price_installments', updated);
                            }}
                            className="h-8 text-sm"
                            disabled={inst.status !== 'paid'}
                          />
                        </td>
                        <td className="p-2">
                          <button
                            onClick={() => {
                              const updated = form.price_installments.filter((_, i) => i !== idx);
                              handleChange('price_installments', updated);
                            }}
                            className="p-1 text-[#B37E6B] hover:bg-[#B37E6B]/10 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-[#8C8C8C] text-center py-4">No payment schedule set. Enter number of payments and click &quot;Generate Plan&quot;.</p>
              )}
            </div>

            {/* Payment Status & Notes */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6">
                <label className="text-xs text-[#8C8C8C] uppercase mb-2 block">Overall Payment Status</label>
                <Select value={form.price_payment_status} onValueChange={(v) => handleChange('price_payment_status', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_collected">Not Collected</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="collected">Collected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6">
                <label className="text-xs text-[#8C8C8C] uppercase mb-2 block">Pricing Notes</label>
                <Textarea
                  value={form.price_notes}
                  onChange={(e) => handleChange('price_notes', e.target.value)}
                  placeholder="Estimates subject to availability..."
                  className="bg-[#EEE9E2] min-h-[60px]"
                />
              </div>
            </div>

            <p className="text-xs text-[#8C8C8C] text-center italic">
              Client approval = agreement to itinerary + pricing + authorization to charge card
            </p>
          </div>
        )}

        {/* Logistics & Documents Tab */}
        {activeSection === 'logistics' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-serif text-xl text-[#1A1A1A]">Logistics & Documents</h2>
                <p className="text-sm text-[#8C8C8C]">
                  Lodging, flights, transfers, confirmations, and documents. Visible items appear on the client portal.
                </p>
              </div>
              <Button
                onClick={async () => {
                  if (!tripId) {
                    toast.error('Please save the trip first');
                    return;
                  }
                  try {
                    const res = await axios.post(`${API}/travels/confirmations`, {
                      trip_id: tripId,
                      confirmation_type: 'hotel',
                      provider_name: '',
                      confirmation_number: '',
                      visible_to_client: true
                    });
                    setConfirmations([res.data, ...confirmations]);
                    toast.success('Confirmation added');
                  } catch (e) {
                    toast.error('Failed to add confirmation');
                  }
                }}
                className="bg-[#2C2C2C]"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Item
              </Button>
            </div>

            {/* Lodging Summary Card */}
            <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6">
              <h3 className="font-medium text-[#1A1A1A] mb-4 flex items-center gap-2">
                <Hotel className="w-5 h-5 text-[#C8A85A]" /> Lodging
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-[#8C8C8C] uppercase mb-1 block">Hotel Name</label>
                  <Input
                    value={form.hotel_name || ''}
                    onChange={(e) => handleChange('hotel_name', e.target.value)}
                    placeholder="e.g., The Ritz Paris"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#8C8C8C] uppercase mb-1 block">Confirmation #</label>
                  <Input
                    value={form.hotel_confirmation || ''}
                    onChange={(e) => handleChange('hotel_confirmation', e.target.value)}
                    placeholder="Booking confirmation"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#8C8C8C] uppercase mb-1 block">Check-in</label>
                  <Input
                    value={form.hotel_checkin || ''}
                    onChange={(e) => handleChange('hotel_checkin', e.target.value)}
                    placeholder="Check-in time"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#8C8C8C] uppercase mb-1 block">Check-out</label>
                  <Input
                    value={form.hotel_checkout || ''}
                    onChange={(e) => handleChange('hotel_checkout', e.target.value)}
                    placeholder="Check-out time"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs text-[#8C8C8C] uppercase mb-1 block">Address</label>
                  <Input
                    value={form.hotel_address || ''}
                    onChange={(e) => handleChange('hotel_address', e.target.value)}
                    placeholder="Hotel address"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs text-[#8C8C8C] uppercase mb-1 block">Notes</label>
                  <Textarea
                    value={form.hotel_notes || ''}
                    onChange={(e) => handleChange('hotel_notes', e.target.value)}
                    placeholder="Special requests, room preferences, etc."
                    rows={2}
                  />
                </div>
              </div>
            </div>

            {/* Flights Summary Card */}
            <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6">
              <h3 className="font-medium text-[#1A1A1A] mb-4 flex items-center gap-2">
                <Plane className="w-5 h-5 text-[#C8A85A]" /> Flights
              </h3>
              <div className="space-y-4">
                {/* Arrival Flight */}
                <div className="p-4 bg-[#EEE9E2] rounded-lg">
                  <p className="text-xs text-[#8C8C8C] uppercase mb-3">Arrival</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Input
                      value={form.arrival_airline || ''}
                      onChange={(e) => handleChange('arrival_airline', e.target.value)}
                      placeholder="Airline"
                    />
                    <Input
                      value={form.arrival_flight_number || ''}
                      onChange={(e) => handleChange('arrival_flight_number', e.target.value)}
                      placeholder="Flight #"
                    />
                    <Input
                      value={form.arrival_departure_airport || ''}
                      onChange={(e) => handleChange('arrival_departure_airport', e.target.value)}
                      placeholder="From (JFK)"
                    />
                    <Input
                      value={form.arrival_arrival_airport || ''}
                      onChange={(e) => handleChange('arrival_arrival_airport', e.target.value)}
                      placeholder="To (CDG)"
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
                    <Input
                      value={form.arrival_departure_time || ''}
                      onChange={(e) => handleChange('arrival_departure_time', e.target.value)}
                      placeholder="Departure time"
                    />
                    <Input
                      value={form.arrival_arrival_time || ''}
                      onChange={(e) => handleChange('arrival_arrival_time', e.target.value)}
                      placeholder="Arrival time"
                    />
                    <Input
                      value={form.arrival_pnr || ''}
                      onChange={(e) => handleChange('arrival_pnr', e.target.value)}
                      placeholder="PNR / Confirmation"
                    />
                  </div>
                </div>
                {/* Departure Flight */}
                <div className="p-4 bg-[#EEE9E2] rounded-lg">
                  <p className="text-xs text-[#8C8C8C] uppercase mb-3">Departure</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Input
                      value={form.departure_airline || ''}
                      onChange={(e) => handleChange('departure_airline', e.target.value)}
                      placeholder="Airline"
                    />
                    <Input
                      value={form.departure_flight_number || ''}
                      onChange={(e) => handleChange('departure_flight_number', e.target.value)}
                      placeholder="Flight #"
                    />
                    <Input
                      value={form.departure_departure_airport || ''}
                      onChange={(e) => handleChange('departure_departure_airport', e.target.value)}
                      placeholder="From (CDG)"
                    />
                    <Input
                      value={form.departure_arrival_airport || ''}
                      onChange={(e) => handleChange('departure_arrival_airport', e.target.value)}
                      placeholder="To (JFK)"
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
                    <Input
                      value={form.departure_departure_time || ''}
                      onChange={(e) => handleChange('departure_departure_time', e.target.value)}
                      placeholder="Departure time"
                    />
                    <Input
                      value={form.departure_arrival_time || ''}
                      onChange={(e) => handleChange('departure_arrival_time', e.target.value)}
                      placeholder="Arrival time"
                    />
                    <Input
                      value={form.departure_pnr || ''}
                      onChange={(e) => handleChange('departure_pnr', e.target.value)}
                      placeholder="PNR / Confirmation"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Confirmations */}
            <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6">
              <h3 className="font-medium text-[#1A1A1A] mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#C8A85A]" /> Additional Confirmations & Documents
              </h3>
              {confirmations.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-10 h-10 mx-auto text-[#8C8C8C] mb-2" />
                  <p className="text-sm text-[#8C8C8C]">No additional confirmations yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {confirmations.map((conf) => (
                    <div key={conf.id} className="p-4 bg-[#EEE9E2] rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <Select
                          value={conf.confirmation_type}
                          onValueChange={async (v) => {
                            try {
                              await axios.patch(`${API}/travels/confirmations/${conf.id}`, { confirmation_type: v });
                              setConfirmations(confirmations.map(c => c.id === conf.id ? { ...c, confirmation_type: v } : c));
                            } catch (e) {
                              toast.error('Failed to update');
                            }
                          }}
                        >
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="hotel">Hotel</SelectItem>
                            <SelectItem value="flight">Flight</SelectItem>
                            <SelectItem value="transfer">Transfer</SelectItem>
                            <SelectItem value="activity">Activity</SelectItem>
                            <SelectItem value="document">Document</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          value={conf.provider_name || ''}
                          placeholder="Provider / Name"
                          onChange={async (e) => {
                            const val = e.target.value;
                            setConfirmations(confirmations.map(c => c.id === conf.id ? { ...c, provider_name: val } : c));
                          }}
                          onBlur={async (e) => {
                            try {
                              await axios.patch(`${API}/travels/confirmations/${conf.id}`, { provider_name: e.target.value });
                            } catch (err) {}
                          }}
                        />
                        <Input
                          value={conf.confirmation_number || ''}
                          placeholder="Confirmation #"
                          onChange={(e) => {
                            const val = e.target.value;
                            setConfirmations(confirmations.map(c => c.id === conf.id ? { ...c, confirmation_number: val } : c));
                          }}
                          onBlur={async (e) => {
                            try {
                              await axios.patch(`${API}/travels/confirmations/${conf.id}`, { confirmation_number: e.target.value });
                            } catch (err) {}
                          }}
                        />
                        <div className="flex items-center gap-2">
                          <label className="flex items-center gap-2 text-sm text-[#595959]">
                            <input
                              type="checkbox"
                              checked={conf.visible_to_client}
                              onChange={async (e) => {
                                try {
                                  await axios.patch(`${API}/travels/confirmations/${conf.id}`, { visible_to_client: e.target.checked });
                                  setConfirmations(confirmations.map(c => c.id === conf.id ? { ...c, visible_to_client: e.target.checked } : c));
                                } catch (err) {}
                              }}
                            />
                            Client visible
                          </label>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={async () => {
                              try {
                                await axios.delete(`${API}/travels/confirmations/${conf.id}`);
                                setConfirmations(confirmations.filter(c => c.id !== conf.id));
                              } catch (e) {}
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-[#B37E6B]" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Weather for Destinations */}
            <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6">
              <h3 className="font-medium text-[#1A1A1A] mb-4 flex items-center gap-2">
                <Sun className="w-5 h-5 text-[#C8A85A]" /> Destination Weather
              </h3>
              {form.destination_segments?.length > 0 ? (
                <div className="space-y-4">
                  {form.destination_segments.filter(s => s.destination).map((seg, idx) => (
                    <div key={idx}>
                      <WeatherDisplay
                        destination={seg.destination}
                        startDate={seg.arrival_date}
                        endDate={seg.departure_date}
                      />
                    </div>
                  ))}
                </div>
              ) : form.destinations ? (
                <WeatherDisplay
                  destination={form.destinations?.split(',')[0]?.trim()}
                  startDate={getTripStartDate(form)}
                  endDate={getTripEndDate(form)}
                />
              ) : (
                <p className="text-sm text-[#8C8C8C]">Add a destination to see weather forecasts</p>
              )}
            </div>

            {/* Discover Places */}
            <DiscoverPlaces
              destination={
                form.destination_segments?.find(s => s.destination)?.destination
                || form.destinations?.split(',')[0]?.trim()
                || ''
              }
              tripId={tripId}
              onAddToItinerary={(place) => {
                toast.success(`Added "${place.name}" — use Itinerary tab to schedule it`);
              }}
            />

            {/* Auto-Plan Day */}
            <AutoPlanDay
              destination={
                form.destination_segments?.find(s => s.destination)?.destination
                || form.destinations?.split(',')[0]?.trim()
                || ''
              }
              onAddToItinerary={(place) => {
                toast.success(`Added "${place.name}" — use Itinerary tab to schedule it`);
              }}
            />

            {/* Route Planner */}
            <RoutePlanner
              destinations={
                form.destination_segments?.filter(s => s.destination).map(s => ({
                  name: s.destination,
                })) || []
              }
            />
          </div>
        )}

        {/* CLIENT LOUNGE TAB - Controls what client sees */}
        {activeSection === 'client_lounge' && (
          <div className="space-y-6" data-testid="client-lounge-tab">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-serif text-xl text-[#1A1A1A]">Client Lounge</h2>
                <p className="text-sm text-[#8C8C8C]">
                  Control what your client sees in their portal. Customize banners, visibility, and messaging.
                </p>
              </div>
              {form.client_portal_token && (
                <Button
                  onClick={() => window.open(`/trip/${clientPortalToken}`, '_blank')}
                  variant="outline"
                  className="border-[#C8A85A] text-[#C8A85A]"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Preview Client Portal
                </Button>
              )}
            </div>

            {/* Client Portal Customization - Banner Name & Journey Overview */}
            <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6">
              <h3 className="font-medium text-[#1A1A1A] mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#C8A85A]" />
                Client Portal Home Customization
              </h3>
              <p className="text-sm text-[#8C8C8C] mb-4">
                Customize the Home page of your client's portal.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-[#8C8C8C] uppercase mb-1 block">Client Banner Name</label>
                  <Input
                    value={form.client_banner_name || ''}
                    onChange={(e) => handleChange('client_banner_name', e.target.value)}
                    placeholder="e.g., Mr. & Mrs. Smith, The Johnson Family"
                    className="bg-[#F5F1EB]"
                  />
                  <p className="text-xs text-[#8C8C8C] mt-1">Optional greeting shown in the status banner. Leave empty to auto-use traveler names.</p>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs text-[#8C8C8C] uppercase">Journey Overview</label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        setGeneratingJourneyOverview(true);
                        try {
                          const res = await axios.post(`${API}/travels/trips/${tripId}/generate-journey-overview`, {
                            destinations: getTripDestinationsDisplay(form) || form.destinations,
                            trip_types: form.trip_types,
                            travel_styles: form.travel_styles,
                            num_travelers: form.num_travelers,
                            start_date: getTripStartDate(form),
                            end_date: getTripEndDate(form)
                          });
                          if (res.data?.journey_overview) {
                            handleChange('journey_overview', res.data.journey_overview);
                            toast.success('Journey overview generated!');
                          }
                        } catch (e) {
                          toast.error('Failed to generate overview');
                          console.error(e);
                        } finally {
                          setGeneratingJourneyOverview(false);
                        }
                      }}
                      disabled={generatingJourneyOverview || !form.destinations}
                      className="text-[#C8A85A] hover:bg-[#C8A85A]/10 h-7 px-2"
                      data-testid="generate-journey-overview-btn"
                    >
                      {generatingJourneyOverview ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                      ) : (
                        <Sparkles className="w-3.5 h-3.5 mr-1" />
                      )}
                      <span className="text-xs">Generate with AI</span>
                    </Button>
                  </div>
                  <Textarea
                    value={form.journey_overview || ''}
                    onChange={(e) => handleChange('journey_overview', e.target.value)}
                    placeholder="Describe the mood and vibe of this journey. This appears on the client's Home page."
                    className="bg-[#F5F1EB]"
                    rows={3}
                  />
                  <p className="text-xs text-[#8C8C8C] mt-1">Sets the emotional tone. Leave empty to hide this section entirely.</p>
                </div>
              </div>
            </div>

            {/* Client Banner Control */}
            <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6">
              <h3 className="font-medium text-[#1A1A1A] mb-4 flex items-center gap-2">
                <Bell className="w-5 h-5 text-[#C8A85A]" />
                Client Banner Message
              </h3>
              <p className="text-sm text-[#8C8C8C] mb-4">
                Send personalized messages that appear at the top of your client's portal.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-[#8C8C8C] uppercase mb-1 block">Banner Message</label>
                  <Textarea
                    value={form.client_banner_message || ''}
                    onChange={(e) => handleChange('client_banner_message', e.target.value)}
                    placeholder="e.g., Your itinerary is ready for review! Let me know if you have any questions."
                    className="bg-[#F5F1EB]"
                    rows={3}
                  />
                </div>
                <div className="flex items-center gap-4">
                  <Button
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/travels/trips/${tripId}/send-banner`, {
                          message: form.client_banner_message
                        });
                        toast.success('Banner sent to client portal');
                      } catch (e) {
                        toast.error('Failed to send banner');
                      }
                    }}
                    className="bg-[#C8A85A]"
                    disabled={!form.client_banner_message}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send Banner
                  </Button>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try {
                        await axios.delete(`${API}/travels/trips/${tripId}/banner`);
                        handleChange('client_banner_message', '');
                        toast.success('Banner cleared');
                      } catch (e) {}
                    }}
                  >
                    Clear Banner
                  </Button>
                </div>
              </div>
            </div>

            {/* Packing List Control */}
            <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6">
              <h3 className="font-medium text-[#1A1A1A] mb-4 flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-[#8DA399]" />
                Packing List
              </h3>
              <p className="text-sm text-[#8C8C8C] mb-4">
                Control when the packing list appears in the client portal.
              </p>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.show_packing_list || false}
                    onChange={(e) => handleChange('show_packing_list', e.target.checked)}
                    className="rounded border-[#8C8C8C]"
                  />
                  <span className="text-sm">Show Packing List in the Member Suite</span>
                </label>
              </div>
            </div>

            {/* AI Assistant Control */}
            <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6">
              <h3 className="font-medium text-[#1A1A1A] mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#C8A85A]" />
                AI Travel Concierge (Client Access)
              </h3>
              <p className="text-sm text-[#8C8C8C] mb-4">
                Allow your client to chat with the AI Travel Concierge for questions about their trip.
              </p>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.show_client_ai_assistant || false}
                    onChange={(e) => handleChange('show_client_ai_assistant', e.target.checked)}
                    className="rounded border-[#8C8C8C]"
                  />
                  <span className="text-sm">Enable AI Concierge in the Member Suite</span>
                </label>
              </div>
            </div>

            {/* Tab Visibility Control */}
            <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6">
              <h3 className="font-medium text-[#1A1A1A] mb-4">Member Suite Tabs</h3>
              <p className="text-sm text-[#8C8C8C] mb-4">
                Choose which tabs are visible to your client. Hidden tabs will show as "Coming Soon" (locked).
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { id: 'itinerary', label: 'Itinerary' },
                  { id: 'estimate', label: 'Estimate' },
                  { id: 'logistics', label: 'Logistics' },
                  { id: 'documents', label: 'Documents' },
                  { id: 'prep', label: 'Prep' },
                  { id: 'packing', label: 'Packing List' },
                  { id: 'chat', label: 'Chat' },
                ].map(tab => (
                  <label key={tab.id} className="flex items-center gap-2 p-3 rounded-lg border border-[rgba(0,0,0,0.06)] hover:bg-[#F5F1EB] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={clientVisibleTabs.includes(tab.id)}
                      onChange={() => {
                        const newTabs = clientVisibleTabs.includes(tab.id)
                          ? clientVisibleTabs.filter(t => t !== tab.id)
                          : [...clientVisibleTabs, tab.id];
                        setClientVisibleTabs(newTabs);
                        axios.patch(`${API}/travels/trips/${tripId}/client-visible-tabs`, newTabs).catch(() => {});
                      }}
                      className="rounded border-[#8C8C8C]"
                    />
                    <span className="text-sm">{tab.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Quick Client Chat */}
            <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-[#1A1A1A] flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-[#C8A85A]" />
                  Client Messages
                </h3>
                <button
                  onClick={() => setActiveSection('messages')}
                  className="text-xs text-[#C8A85A] hover:underline"
                  data-testid="go-to-messages-tab"
                >
                  View All Messages
                </button>
              </div>
              <div className="space-y-2 max-h-[200px] overflow-y-auto mb-3" data-testid="client-lounge-chat-preview">
                {clientChatMessages.length === 0 ? (
                  <p className="text-sm text-[#8C8C8C] text-center py-4">No messages yet. Start a conversation from the Messages tab.</p>
                ) : (
                  clientChatMessages.slice(-5).map((msg, i) => (
                    <div key={msg.id || i} className={`p-2 rounded-lg text-sm ${
                      msg.sender === 'advisor' ? 'bg-[#C8A85A]/10 ml-8' : 'bg-[#F5F1EB] mr-8'
                    }`}>
                      <span className="text-xs font-medium text-[#8C8C8C]">{msg.sender === 'advisor' ? 'You' : 'Client'}</span>
                      <p className="text-[#1A1A1A]">{msg.message}</p>
                    </div>
                  ))
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newClientMessage}
                  onChange={(e) => setNewClientMessage(e.target.value)}
                  placeholder="Quick message to client..."
                  className="bg-[#F5F1EB] flex-1"
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendClientMessage(); } }}
                  data-testid="client-lounge-chat-input"
                />
                <Button
                  onClick={sendClientMessage}
                  disabled={!newClientMessage.trim()}
                  className="bg-[#C8A85A] hover:bg-[#B39049] text-white"
                  data-testid="client-lounge-chat-send"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}


        {/* MESSAGES TAB - Client Chat + Internal Notes */}
        {activeSection === 'messages' && (
          <div className="space-y-6" data-testid="messages-tab">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-serif text-xl text-[#1A1A1A]">Messages</h2>
                <p className="text-sm text-[#8C8C8C]">
                  Client chat and internal planning notes for this trip.
                </p>
              </div>
            </div>

            {/* Client Chat Thread */}
            <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6">
              <h3 className="font-medium text-[#1A1A1A] mb-4 flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-[#C8A85A]" />
                Client Chat
              </h3>
              <p className="text-xs text-[#8C8C8C] mb-4">
                Messages here are visible to the client in their portal.
              </p>
              <div className="space-y-3 max-h-[400px] overflow-y-auto mb-4 p-3 bg-[#F5F1EB] rounded-lg" data-testid="client-chat-thread">
                {clientChatMessages.length === 0 ? (
                  <p className="text-sm text-[#8C8C8C] text-center py-8">No messages yet. Send a message to start the conversation.</p>
                ) : (
                  clientChatMessages.map((msg, i) => (
                    <div key={msg.id || i} className={`flex ${msg.sender === 'advisor' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-lg p-3 group relative ${
                        msg.sender === 'advisor' 
                          ? 'bg-[#C8A85A] text-white' 
                          : 'bg-white border border-[rgba(0,0,0,0.06)]'
                      }`}>
                        {editingMsgId === msg.id ? (
                          <div className="space-y-2">
                            <Input
                              value={editingMsgText}
                              onChange={(e) => setEditingMsgText(e.target.value)}
                              className="text-sm bg-white/20 border-white/30 text-inherit"
                              autoFocus
                              onKeyDown={(e) => { if (e.key === 'Enter') handleEditMessage(msg.id, editingMsgText); if (e.key === 'Escape') { setEditingMsgId(null); setEditingMsgText(''); } }}
                              data-testid={`edit-msg-input-${msg.id}`}
                            />
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => { setEditingMsgId(null); setEditingMsgText(''); }} className="text-xs opacity-70 hover:opacity-100">Cancel</button>
                              <button onClick={() => handleEditMessage(msg.id, editingMsgText)} className="text-xs font-medium opacity-90 hover:opacity-100" data-testid={`save-edit-${msg.id}`}>Save</button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                            <p className={`text-xs mt-1 ${msg.sender === 'advisor' ? 'text-white/60' : 'text-[#8C8C8C]'}`}>
                              {msg.sender === 'advisor' ? 'You' : 'Client'} {msg.created_at ? `\u2022 ${new Date(msg.created_at).toLocaleString()}` : ''}
                              {msg.edited_at ? ' (edited)' : ''}
                            </p>
                            {msg.sender === 'advisor' && (
                              <div className="absolute top-1 right-1 hidden group-hover:flex gap-0.5">
                                <button
                                  onClick={() => { setEditingMsgId(msg.id); setEditingMsgText(msg.message); }}
                                  className="p-1 rounded bg-white/20 hover:bg-white/30 transition-colors"
                                  title="Edit"
                                  data-testid={`edit-msg-${msg.id}`}
                                >
                                  <Edit2 className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => handleDeleteMessage(msg.id)}
                                  className="p-1 rounded bg-white/20 hover:bg-red-500/50 transition-colors"
                                  title="Delete"
                                  data-testid={`delete-msg-${msg.id}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newClientMessage}
                  onChange={(e) => setNewClientMessage(e.target.value)}
                  placeholder="Type a message to your client..."
                  className="bg-[#F5F1EB] flex-1"
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendClientMessage(); } }}
                  data-testid="client-chat-input"
                />
                <Button
                  onClick={sendClientMessage}
                  disabled={!newClientMessage.trim()}
                  className="bg-[#C8A85A] hover:bg-[#B39049] text-white"
                  data-testid="client-chat-send-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Internal Notes Thread */}
            <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6">
              <h3 className="font-medium text-[#1A1A1A] mb-4 flex items-center gap-2">
                <StickyNote className="w-5 h-5 text-[#8DA399]" />
                Internal Notes
              </h3>
              <p className="text-xs text-[#8C8C8C] mb-4">
                Private notes not visible to the client.
              </p>
              <div className="space-y-3 max-h-[300px] overflow-y-auto mb-4 p-3 bg-[#F5F1EB] rounded-lg" data-testid="internal-notes-thread">
                {internalMessages.length === 0 ? (
                  <p className="text-sm text-[#8C8C8C] text-center py-4">No internal notes yet.</p>
                ) : (
                  internalMessages.map((msg, i) => (
                    <div key={msg.id || i} className="bg-white rounded-lg p-3 border border-[rgba(0,0,0,0.06)] group relative">
                      {editingMsgId === msg.id ? (
                        <div className="space-y-2">
                          <Input
                            value={editingMsgText}
                            onChange={(e) => setEditingMsgText(e.target.value)}
                            className="text-sm"
                            autoFocus
                            onKeyDown={(e) => { if (e.key === 'Enter') handleEditMessage(msg.id, editingMsgText); if (e.key === 'Escape') { setEditingMsgId(null); setEditingMsgText(''); } }}
                            data-testid={`edit-note-input-${msg.id}`}
                          />
                          <div className="flex gap-2 justify-end">
                            <button onClick={() => { setEditingMsgId(null); setEditingMsgText(''); }} className="text-xs text-[#8C8C8C] hover:text-[#1A1A1A]">Cancel</button>
                            <button onClick={() => handleEditMessage(msg.id, editingMsgText)} className="text-xs text-[#C8A85A] font-medium" data-testid={`save-note-edit-${msg.id}`}>Save</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="text-sm whitespace-pre-wrap text-[#1A1A1A]">{msg.message}</p>
                          <p className="text-xs text-[#8C8C8C] mt-1">
                            {msg.created_at ? new Date(msg.created_at).toLocaleString() : ''}
                            {msg.edited_at ? ' (edited)' : ''}
                          </p>
                          <div className="absolute top-2 right-2 hidden group-hover:flex gap-1">
                            <button
                              onClick={() => { setEditingMsgId(msg.id); setEditingMsgText(msg.message); }}
                              className="p-1 rounded hover:bg-[#F5F1EB] transition-colors"
                              title="Edit"
                              data-testid={`edit-note-${msg.id}`}
                            >
                              <Edit2 className="w-3 h-3 text-[#8C8C8C]" />
                            </button>
                            <button
                              onClick={() => handleDeleteMessage(msg.id)}
                              className="p-1 rounded hover:bg-red-50 transition-colors"
                              title="Delete"
                              data-testid={`delete-note-${msg.id}`}
                            >
                              <Trash2 className="w-3 h-3 text-red-400" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newInternalMessage}
                  onChange={(e) => setNewInternalMessage(e.target.value)}
                  placeholder="Add internal note..."
                  className="bg-[#F5F1EB] flex-1"
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendInternalMessage(); } }}
                  data-testid="internal-note-input"
                />
                <Button
                  onClick={sendInternalMessage}
                  disabled={!newInternalMessage.trim()}
                  variant="outline"
                  className="border-[#8DA399] text-[#8DA399]"
                  data-testid="internal-note-send-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* AGREEMENTS TAB */}
        {activeSection === 'agreements' && (
          <div className="space-y-6" data-testid="agreements-tab">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-serif text-xl text-[#1A1A1A]">Agreements & Contracts</h2>
                <p className="text-sm text-[#8C8C8C]">
                  Manage travel agreements, terms, and client signatures.
                </p>
              </div>
            </div>

            {/* Agreement Status */}
            <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6">
              <h3 className="font-medium text-[#1A1A1A] mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#C8A85A]" />
                Agreement Status
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-[#F5F1EB] rounded-lg">
                  <div>
                    <p className="font-medium">Travel Agreement</p>
                    <p className="text-sm text-[#8C8C8C]">Terms and conditions for this trip</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm ${
                    form.terms_acknowledged 
                      ? 'bg-[#8DA399]/20 text-[#8DA399]' 
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {form.terms_acknowledged ? 'Signed' : 'Pending'}
                  </span>
                </div>
                {form.terms_acknowledged && form.terms_acknowledged_at && (
                  <p className="text-xs text-[#8C8C8C]">
                    Signed on {formatDate(form.terms_acknowledged_at)}
                  </p>
                )}
              </div>
            </div>

            {/* Contract Upload Section */}
            <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6">
              <h3 className="font-medium text-[#1A1A1A] mb-4">Contract Documents</h3>
              <p className="text-sm text-[#8C8C8C] mb-4">
                Upload signed contracts and agreements for record keeping.
              </p>
              <div className="border-2 border-dashed border-[rgba(0,0,0,0.1)] rounded-lg p-8 text-center">
                <FileText className="w-12 h-12 mx-auto mb-3 text-[#8C8C8C]" />
                <p className="text-sm text-[#8C8C8C]">Drag & drop files here or click to upload</p>
                <p className="text-xs text-[#8C8C8C] mt-1">PDF, DOC, DOCX up to 10MB</p>
              </div>
            </div>
          </div>
        )}

        {/* ADVISOR NOTES TAB */}
        {activeSection === 'advisor_notes' && (
          <div className="space-y-6" data-testid="advisor-notes-tab">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-serif text-xl text-[#1A1A1A]">Advisor Notes</h2>
                <p className="text-sm text-[#8C8C8C]">
                  Private notes and planning details. These are not visible to clients.
                </p>
              </div>
            </div>

            {/* Internal Notes */}
            <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6">
              <h3 className="font-medium text-[#1A1A1A] mb-4 flex items-center gap-2">
                <StickyNote className="w-5 h-5 text-[#C8A85A]" />
                Internal Planning Notes
              </h3>
              <Textarea
                value={form.advisor_notes || ''}
                onChange={(e) => handleChange('advisor_notes', e.target.value)}
                placeholder="Add private notes about this trip planning... (not visible to clients)"
                className="bg-[#F5F1EB] min-h-[200px]"
              />
            </div>

            {/* Client Preferences */}
            <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6">
              <h3 className="font-medium text-[#1A1A1A] mb-4">Client Preferences & Requirements</h3>
              <Textarea
                value={form.client_preferences || ''}
                onChange={(e) => handleChange('client_preferences', e.target.value)}
                placeholder="Special requests, dietary restrictions, accessibility needs, etc."
                className="bg-[#F5F1EB] min-h-[100px]"
              />
            </div>

            {/* Vendor Contacts */}
            <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6">
              <h3 className="font-medium text-[#1A1A1A] mb-4">Vendor Contacts & Bookings</h3>
              <Textarea
                value={form.vendor_contacts || ''}
                onChange={(e) => handleChange('vendor_contacts', e.target.value)}
                placeholder="Contact info for hotels, tour operators, restaurants..."
                className="bg-[#F5F1EB] min-h-[100px]"
              />
            </div>
          </div>
        )}
      </div>

      {/* Generate Dialog */}
      <AlertDialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Generate Itinerary</AlertDialogTitle>
            <AlertDialogDescription>
              You have {itinerary.length} day(s). Choose how to proceed:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 py-4">
            <button onClick={() => generateItinerary('append')} className="w-full p-4 text-left rounded-lg border hover:border-[#8DA399] hover:bg-[#8DA399]/5">
              <p className="font-medium">Append</p>
              <p className="text-xs text-[#8C8C8C]">Add new content without changing existing</p>
            </button>
            <button onClick={() => generateItinerary('replace')} className="w-full p-4 text-left rounded-lg border border-[#B37E6B]/30 hover:border-[#B37E6B] hover:bg-[#B37E6B]/5">
              <p className="font-medium text-[#B37E6B]">Replace All</p>
              <p className="text-xs text-[#8C8C8C]">Delete existing and generate fresh</p>
            </button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Republish Confirmation Dialog - Warns about approval reset */}
      <AlertDialog open={showRepublishConfirm} onOpenChange={setShowRepublishConfirm}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 font-serif">
              <AlertCircle className="w-5 h-5 text-[#C8A85A]" />
              Reset Client Approval?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#595959]">
              This trip has already been approved by the client. Republishing will <span className="font-medium text-[#B37E6B]">reset their approval status</span> and they will need to review and approve the itinerary again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="p-4 bg-[#F5F1EB] rounded-lg my-4">
            <p className="text-sm text-[#595959]">
              <span className="font-medium">Current Status:</span> Approved
              {form.approved_at && (
                <span className="text-xs text-[#8C8C8C] ml-2">
                  ({formatDate(form.approved_at)})
                </span>
              )}
            </p>
            <p className="text-sm text-[#595959] mt-1">
              <span className="font-medium">After Republish:</span> Draft (pending approval)
            </p>
          </div>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="border-[#8C8C8C] text-[#8C8C8C]">Cancel</AlertDialogCancel>
            <Button 
              onClick={handleConfirmedRepublish}
              className="bg-[#C8A85A] hover:bg-[#B39049] text-white"
              data-testid="confirm-republish-btn"
            >
              Reset Approval & Republish
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </TripPlanningProvider>
  );
}
